import type { Locale } from "@/i18n/locales";

export type LegalDoc = { title: string; updated: string; markdown: string };

type FaqDoc = {
  title: string;
  intro: string;
  groups: { heading: string; items: { q: string; a: string }[] }[];
};

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const privacyNl: LegalDoc = {
  title: "Privacyverklaring Vaxy",
  updated: "januari 2025",
  markdown: `Deze privacyverklaring is opgesteld door Vaccinatieboekje B.V., handelend onder de naam Vaxy (hierna: "Vaxy" of "wij"). Vaxy hecht groot belang aan de bescherming van persoonsgegevens en handelt conform de Algemene Verordening Gegevensbescherming (AVG) en overige toepasselijke wet- en regelgeving.

Deze verklaring is van toepassing op de mobiele app Vaxy en de websites vaxy.app en vaccinatieboekje.com (gezamenlijk: "de app"). Door gebruik te maken van de app stem je in met deze privacyverklaring.

## 1. Verwerkingsverantwoordelijke en toepassingsgebied

Vaccinatieboekje B.V., statutair gevestigd te Amsterdam, is de verwerkingsverantwoordelijke voor de verwerking van persoonsgegevens via de app. Deze verklaring beschrijft hoe Vaxy persoonsgegevens verzamelt, gebruikt, opslaat en beschermt, evenals de rechten van gebruikers. Aan deze verklaring kunnen geen rechten worden ontleend.

## 2. Gegevensverwerking en doeleinden

Vaxy verwerkt alleen persoonsgegevens die noodzakelijk zijn voor de werking van de app, waaronder:

- Identificatiegegevens: naam, geboortedatum, geslacht, e-mailadres en profielfoto.
- Gezondheidsgegevens: vaccinatie-informatie die je zelf toevoegt (zoals ziekte, datum, merk, batchnummer, geldigheidsduur).
- Technische gegevens: apparaatinformatie en gebruiksstatistieken die nodig zijn voor functionaliteit en beveiliging.

Deze gegevens worden verwerkt voor:

- Het aanmaken en beheren van jouw account.
- Het tonen, bewaren en bijwerken van jouw vaccinatieoverzicht.
- Het verzenden van noodzakelijke serviceberichten.
- Het uitnodigen tot vrijwillige enquêtes over de app.
- Het verbeteren van de stabiliteit, veiligheid en betrouwbaarheid van de app.

De verwerking vindt plaats op basis van:

- Toestemming (artikel 6 lid 1 sub a AVG).
- Uitvoering van de overeenkomst (artikel 6 lid 1 sub b AVG).
- Gerechtvaardigd belang bij verbetering van de dienstverlening (artikel 6 lid 1 sub f AVG).

Voor zover gezondheidsgegevens worden verwerkt, gebeurt dit uitsluitend met uitdrukkelijke toestemming als bedoeld in artikel 9 lid 2 sub a AVG. Je kunt deze toestemming op elk moment intrekken via de instellingen van je account.

## 3. Gebruik van foto's en camera

De app kan toegang vragen tot jouw foto's of camera om vaccinatiebewijzen toe te voegen of te fotograferen. Deze toegang is optioneel en wordt pas verleend na jouw expliciete toestemming. De foto's blijven binnen jouw account en worden niet gedeeld met derden, tenzij jij daar zelf voor kiest.

## 4. Automatische herkenning met kunstmatige intelligentie (AI)

Om het invoeren van vaccinaties eenvoudiger te maken, gebruikt Vaxy kunstmatige intelligentie (AI) om vaccinatiebewijzen automatisch te herkennen. De AI verwerkt alleen tekst die zichtbaar is op het vaccinatiebewijs, gebruikt geen gezichtsherkenning of andere persoonlijke informatie, traint geen modellen met jouw data en deelt niets met derden. Deze verwerking vindt plaats binnen onze beveiligde infrastructuur en is uitsluitend bedoeld om jou te helpen sneller en nauwkeuriger jouw vaccinaties vast te leggen.

## 5. Serviceberichten en nieuwsbrieven

Bij het aanmaken van een account stem je in met het ontvangen van noodzakelijke berichten die essentieel zijn voor de werking van de app, zoals beveiligingsmeldingen en updates. Deze communicatie is onderdeel van de uitvoering van de overeenkomst en kan niet worden uitgeschakeld zolang het account actief is.

We mogen je per e-mail uitnodigen voor vrijwillige enquêtes over de app, zodat we de dienst kunnen verbeteren. Deelname is vrijwillig. Deze berichten zijn gebaseerd op ons gerechtvaardigd belang (artikel 6 lid 1 sub f AVG) en zijn geen promotionele berichten. Je kunt je altijd afmelden via de afmeldlink in de e-mail. Antwoorden worden niet gekoppeld aan gezondheidsgegevens uit je account, tenzij je die zelf invult.

Daarnaast kun je je vrijwillig aanmelden voor onze nieuwsbrief. Deze bevat nieuws en updates over onze diensten. Je kunt je op elk moment afmelden via de afmeldlink of in de app onder notificatie instellingen. Promotionele berichten worden alleen verzonden met jouw voorafgaande toestemming.

## 6. Geanonimiseerd onderzoek en datagebruik

Vaxy kan geanonimiseerde of geaggregeerde gegevens gebruiken voor statistisch of wetenschappelijk onderzoek, bijvoorbeeld om inzicht te krijgen in vaccinatiegedrag of trends in preventieve zorg. Deze gegevens zijn niet herleidbaar tot individuele personen. Resultaten worden alleen gepresenteerd in geaggregeerde of volledig anonieme vorm. De verwerking is gebaseerd op het gerechtvaardigd belang van Vaxy om haar diensten te verbeteren en bij te dragen aan algemeen onderzoek zonder inbreuk op de privacy.

## 7. Verwerkers en subverwerkers

Vaxy maakt gebruik van zorgvuldig geselecteerde verwerkers en subverwerkers (zoals hosting-, e-mail- en analysetools). Met iedere partij is een verwerkersovereenkomst gesloten die voldoet aan artikel 28 AVG. Deze partijen verwerken gegevens uitsluitend in opdracht van Vaxy en onder haar toezicht. Een actuele lijst van verwerkers is op verzoek beschikbaar via info@vaccinatieboekje.com.

## 8. Locatie van gegevensopslag

Persoonsgegevens worden in beginsel verwerkt binnen de Europese Economische Ruimte (EER). Indien verwerking buiten de EER plaatsvindt, gebeurt dit uitsluitend met passende waarborgen, zoals EU-modelcontractbepalingen (SCC's) of een adequaatheidsbesluit van de Europese Commissie.

## 9. Externe platforms (App Stores)

De app wordt verspreid via de appstores van Apple en Google. Vaxy heeft geen invloed op de gegevensverwerking door deze partijen. Lees daarom ook hun eigen privacyverklaringen. Vaxy aanvaardt geen aansprakelijkheid voor schade die voortvloeit uit of samenhangt met het gebruik van deze externe diensten.

## 10. Cookies

De app en website gebruiken cookies om de functionaliteit te verbeteren en statistieken bij te houden. Bij het eerste gebruik wordt toestemming gevraagd voor niet-essentiële cookies. Je kunt cookies uitschakelen via je browserinstellingen, maar sommige functies werken dan mogelijk niet correct.

## 11. Analyse- en meetdiensten

### 11.1 Google Analytics

Vaxy gebruikt Google Analytics om geanonimiseerde informatie te verzamelen over het gebruik van de app. Met Google is een verwerkersovereenkomst afgesloten. IP-adressen worden geanonimiseerd en gegevens worden niet gebruikt voor andere Google-diensten.

### 11.2 Mixpanel Analytics

Vaxy gebruikt Mixpanel om inzicht te krijgen in het gebruik van de app. Hierbij worden apparaat-ID's en gebruiksstatistieken verwerkt. Mixpanel anonimiseert IP-adressen en mag de data niet gebruiken voor eigen doeleinden. Meer informatie: mixpanel.com/legal/privacy-policy.

## 12. Beveiliging van gegevens

Vaxy neemt passende technische en organisatorische maatregelen om persoonsgegevens te beschermen, waaronder:

- Versleutelde opslag van gegevens (encryptie);
- Beveiligde verbindingen (TLS/SSL);
- Interne toegangsbeperkingen en logging;
- Regelmatige beveiligingscontroles.

Ondanks deze maatregelen kan Vaxy geen absolute beveiliging garanderen. Gebruik van de app is op eigen risico. Vaxy is niet aansprakelijk voor schade als gevolg van onbevoegde toegang of verlies van gegevens, tenzij sprake is van opzet of grove nalatigheid aan haar zijde.

## 13. Bewaartermijnen

Persoonsgegevens worden niet langer bewaard dan noodzakelijk is voor het doel waarvoor ze zijn verzameld, tenzij een wettelijke verplichting een langere bewaartermijn vereist. Geanonimiseerde gegevens kunnen voor statistische doeleinden langer worden bewaard.

## 14. Wijzigingen

Vaxy behoudt zich het recht voor deze privacyverklaring te wijzigen. De meest recente versie is altijd beschikbaar in de app en op de website. Bij ingrijpende wijzigingen word je hiervan op de hoogte gebracht.

## 15. Jouw rechten

Je hebt het recht om:

- je persoonsgegevens in te zien;
- onjuistheden te laten corrigeren;
- je gegevens te laten verwijderen;
- bezwaar te maken tegen verwerking of direct marketing;
- je gegevens over te laten dragen aan een derde partij;
- de verwerking tijdelijk te laten beperken.

Verzoeken kunnen worden ingediend via info@vaccinatieboekje.com. Vaxy kan vragen om identificatie (met afgeschermd BSN en pasfoto). Wij reageren binnen 30 dagen, tenzij de complexiteit van het verzoek een verlenging vereist.

## 16. Medische disclaimer

De informatie in de app Vaxy is uitsluitend bedoeld ter ondersteuning van persoonlijke gezondheidsadministratie en vormt geen medisch advies. Vaxy is geen zorgaanbieder. Raadpleeg bij medische vragen altijd een arts of bevoegde zorgprofessional. Vaxy aanvaardt geen aansprakelijkheid voor schade voortvloeiend uit het gebruik van de verstrekte informatie.

## 17. DPIA en verantwoordelijkheden

Vaxy heeft een Data Protection Impact Assessment (DPIA) uitgevoerd voor de kernfunctionaliteiten van de app. Vaccinatieboekje B.V. treedt op als verwerkingsverantwoordelijke voor de gegevens die via de app worden verwerkt. Externe partners of zorgaanbieders die via koppelingen gegevens ontvangen of verstrekken, zijn zelfstandig verantwoordelijk voor naleving van de AVG voor hun eigen verwerkingen.

## 18. Klachten

Indien je van mening bent dat Vaxy niet handelt in overeenstemming met de privacywetgeving, kun je een klacht indienen bij de Autoriteit Persoonsgegevens.

## 19. Aansprakelijkheid en toepasselijk recht

Vaxy spant zich in om juiste en actuele informatie te bieden, maar garandeert niet dat de app te allen tijde foutloos of ononderbroken functioneert. Gebruik van de app is op eigen risico. Vaxy kan niet aansprakelijk worden gehouden voor directe of indirecte schade die voortvloeit uit het gebruik van de app of de weergegeven informatie, behoudens opzet of grove nalatigheid.

Op deze privacyverklaring is uitsluitend Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter te Amsterdam.

## 20. Contact

Vaccinatieboekje B.V.  
Handelend onder de naam Vaxy  
info@vaccinatieboekje.com  
vaxy.app | vaccinatieboekje.com`,
};

const privacyEn: LegalDoc = {
  title: "Vaxy Privacy Statement",
  updated: "January 2025",
  markdown: `This privacy statement has been prepared by Vaccinatieboekje B.V., trading under the name Vaxy (hereinafter: "Vaxy" or "we"). Vaxy attaches great importance to the protection of personal data and acts in accordance with the General Data Protection Regulation (GDPR) and other applicable laws and regulations.

This statement applies to the Vaxy mobile app and the websites vaxy.app and vaccinatieboekje.com (together: "the app"). By using the app, you agree to this privacy statement.

## 1. Controller and scope

Vaccinatieboekje B.V., with its statutory seat in Amsterdam, is the controller for the processing of personal data through the app. This statement describes how Vaxy collects, uses, stores and protects personal data, as well as users' rights. No rights can be derived from this statement.

## 2. Data processing and purposes

Vaxy only processes personal data that is necessary for the operation of the app, including:

- Identification data: name, date of birth, gender, email address and profile photo.
- Health data: vaccination information that you add yourself (such as disease, date, brand, batch number and validity period).
- Technical data: device information and usage statistics required for functionality and security.

These data are processed for:

- Creating and managing your account.
- Showing, storing and updating your vaccination overview.
- Sending necessary service messages.
- Inviting you to voluntary surveys about the app.
- Improving the stability, security and reliability of the app.

Processing takes place on the basis of:

- Consent (Article 6(1)(a) GDPR).
- Performance of the contract (Article 6(1)(b) GDPR).
- Legitimate interest in improving the service (Article 6(1)(f) GDPR).

To the extent health data are processed, this is done only with explicit consent as referred to in Article 9(2)(a) GDPR. You can withdraw this consent at any time through your account settings.

## 3. Use of photos and camera

The app may request access to your photos or camera to add or photograph vaccination records. This access is optional and is granted only after your explicit consent. The photos remain within your account and are not shared with third parties unless you choose to do so yourself.

## 4. Automatic recognition with artificial intelligence (AI)

To make entering vaccinations easier, Vaxy uses artificial intelligence (AI) to automatically recognize vaccination records. The AI only processes text visible on the vaccination record, does not use facial recognition or other personal information, does not train models with your data and does not share anything with third parties. This processing takes place within our secure infrastructure and is intended only to help you record your vaccinations faster and more accurately.

## 5. Service messages and newsletters

When creating an account, you agree to receive necessary messages that are essential for the operation of the app, such as security notices and updates. This communication is part of the performance of the contract and cannot be disabled while the account is active.

We may email you invitations to voluntary surveys about the app, so we can improve the service. Participation is voluntary. These messages are based on our legitimate interest (Article 6(1)(f) GDPR) and are not promotional messages. You can unsubscribe at any time via the unsubscribe link in the email. Responses are not linked to health data in your account, unless you enter that data yourself.

You may also voluntarily subscribe to our newsletter. It contains news and updates about our services. You can unsubscribe at any time via the unsubscribe link or in the app under notification settings. Promotional messages are sent only with your prior consent.

## 6. Anonymized research and data use

Vaxy may use anonymized or aggregated data for statistical or scientific research, for example to gain insight into vaccination behavior or trends in preventive care. These data cannot be traced back to individual persons. Results are presented only in aggregated or fully anonymous form. Processing is based on Vaxy's legitimate interest in improving its services and contributing to general research without infringing privacy.

## 7. Processors and subprocessors

Vaxy uses carefully selected processors and subprocessors (such as hosting, email and analytics tools). A processing agreement that complies with Article 28 GDPR has been concluded with each party. These parties process data only on behalf of Vaxy and under its supervision. An up-to-date list of processors is available on request via info@vaccinatieboekje.com.

## 8. Location of data storage

Personal data are generally processed within the European Economic Area (EEA). If processing takes place outside the EEA, this is done only with appropriate safeguards, such as EU Standard Contractual Clauses (SCCs) or an adequacy decision by the European Commission.

## 9. External platforms (App Stores)

The app is distributed through the app stores of Apple and Google. Vaxy has no influence over data processing by these parties. Please also read their own privacy statements. Vaxy accepts no liability for damage arising from or related to the use of these external services.

## 10. Cookies

The app and website use cookies to improve functionality and keep statistics. Consent is requested for non-essential cookies on first use. You can disable cookies through your browser settings, but some functions may then not work correctly.

## 11. Analytics and measurement services

### 11.1 Google Analytics

Vaxy uses Google Analytics to collect anonymized information about app use. A processing agreement has been concluded with Google. IP addresses are anonymized and data are not used for other Google services.

### 11.2 Mixpanel Analytics

Vaxy uses Mixpanel to understand use of the app. Device IDs and usage statistics are processed for this purpose. Mixpanel anonymizes IP addresses and may not use the data for its own purposes. More information: mixpanel.com/legal/privacy-policy.

## 12. Data security

Vaxy takes appropriate technical and organizational measures to protect personal data, including:

- Encrypted data storage;
- Secure connections (TLS/SSL);
- Internal access restrictions and logging;
- Regular security checks.

Despite these measures, Vaxy cannot guarantee absolute security. Use of the app is at your own risk. Vaxy is not liable for damage resulting from unauthorized access to or loss of data, unless caused by intent or gross negligence on its part.

## 13. Retention periods

Personal data are not kept longer than necessary for the purpose for which they were collected, unless a legal obligation requires a longer retention period. Anonymized data may be retained longer for statistical purposes.

## 14. Changes

Vaxy reserves the right to amend this privacy statement. The most recent version is always available in the app and on the website. You will be informed of significant changes.

## 15. Your rights

You have the right to:

- access your personal data;
- have inaccuracies corrected;
- have your data deleted;
- object to processing or direct marketing;
- have your data transferred to a third party;
- temporarily restrict processing.

Requests can be submitted via info@vaccinatieboekje.com. Vaxy may ask for identification (with BSN and passport photo shielded). We respond within 30 days, unless the complexity of the request requires an extension.

## 16. Medical disclaimer

The information in the Vaxy app is intended solely to support personal health administration and does not constitute medical advice. Vaxy is not a healthcare provider. Always consult a doctor or qualified healthcare professional for medical questions. Vaxy accepts no liability for damage arising from the use of the information provided.

## 17. DPIA and responsibilities

Vaxy has carried out a Data Protection Impact Assessment (DPIA) for the app's core functionalities. Vaccinatieboekje B.V. acts as controller for the data processed through the app. External partners or healthcare providers that receive or provide data through integrations are independently responsible for GDPR compliance for their own processing.

## 18. Complaints

If you believe that Vaxy is not acting in accordance with privacy legislation, you may lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).

## 19. Liability and applicable law

Vaxy endeavors to provide accurate and up-to-date information, but does not guarantee that the app will function error-free or without interruption at all times. Use of the app is at your own risk. Vaxy cannot be held liable for direct or indirect damage arising from use of the app or the information displayed, except in cases of intent or gross negligence.

This privacy statement is governed exclusively by Dutch law. Disputes will be submitted to the competent court in Amsterdam.

## 20. Contact

Vaccinatieboekje B.V.  
Trading under the name Vaxy  
info@vaccinatieboekje.com  
vaxy.app | vaccinatieboekje.com`,
};

const privacyDe: LegalDoc = {
  title: "Datenschutzerklaerung Vaxy",
  updated: "Januar 2025",
  markdown: `Diese Datenschutzerklaerung wurde von Vaccinatieboekje B.V., handelnd unter dem Namen Vaxy (nachfolgend: "Vaxy" oder "wir"), erstellt. Vaxy legt grossen Wert auf den Schutz personenbezogener Daten und handelt im Einklang mit der Datenschutz-Grundverordnung (DSGVO) und sonstigen anwendbaren Gesetzen und Vorschriften.

Diese Erklaerung gilt fuer die mobile App Vaxy sowie die Websites vaxy.app und vaccinatieboekje.com (gemeinsam: "die App"). Durch die Nutzung der App stimmst du dieser Datenschutzerklaerung zu.

## 1. Verantwortlicher und Anwendungsbereich

Vaccinatieboekje B.V. mit satzungsmaessigem Sitz in Amsterdam ist Verantwortlicher fuer die Verarbeitung personenbezogener Daten ueber die App. Diese Erklaerung beschreibt, wie Vaxy personenbezogene Daten erhebt, nutzt, speichert und schuetzt, sowie die Rechte der Nutzer. Aus dieser Erklaerung koennen keine Rechte abgeleitet werden.

## 2. Datenverarbeitung und Zwecke

Vaxy verarbeitet nur personenbezogene Daten, die fuer den Betrieb der App erforderlich sind, darunter:

- Identifikationsdaten: Name, Geburtsdatum, Geschlecht, E-Mail-Adresse und Profilfoto.
- Gesundheitsdaten: Impfinformationen, die du selbst hinzufuegst (z. B. Krankheit, Datum, Marke, Chargennummer, Gueltigkeitsdauer).
- Technische Daten: Geraeteinformationen und Nutzungsstatistiken, die fuer Funktionalitaet und Sicherheit erforderlich sind.

Diese Daten werden verarbeitet fuer:

- Das Erstellen und Verwalten deines Kontos.
- Das Anzeigen, Speichern und Aktualisieren deiner Impfuebersicht.
- Das Versenden notwendiger Servicenachrichten.
- Die Einladung zu freiwilligen Umfragen ueber die App.
- Die Verbesserung der Stabilitaet, Sicherheit und Zuverlaessigkeit der App.

Die Verarbeitung erfolgt auf Grundlage von:

- Einwilligung (Artikel 6 Absatz 1 Buchstabe a DSGVO).
- Erfuellung des Vertrags (Artikel 6 Absatz 1 Buchstabe b DSGVO).
- Berechtigtem Interesse an der Verbesserung der Dienstleistung (Artikel 6 Absatz 1 Buchstabe f DSGVO).

Soweit Gesundheitsdaten verarbeitet werden, geschieht dies ausschliesslich mit ausdruecklicher Einwilligung im Sinne von Artikel 9 Absatz 2 Buchstabe a DSGVO. Du kannst diese Einwilligung jederzeit ueber die Einstellungen deines Kontos widerrufen.

## 3. Nutzung von Fotos und Kamera

Die App kann Zugriff auf deine Fotos oder Kamera anfordern, um Impfnachweise hinzuzufuegen oder zu fotografieren. Dieser Zugriff ist optional und wird erst nach deiner ausdruecklichen Einwilligung erteilt. Die Fotos bleiben in deinem Konto und werden nicht an Dritte weitergegeben, es sei denn, du entscheidest dich selbst dafuer.

## 4. Automatische Erkennung mit kuenstlicher Intelligenz (KI)

Um die Eingabe von Impfungen zu erleichtern, nutzt Vaxy kuenstliche Intelligenz (KI), um Impfnachweise automatisch zu erkennen. Die KI verarbeitet nur Text, der auf dem Impfnachweis sichtbar ist, verwendet keine Gesichtserkennung oder andere persoenliche Informationen, trainiert keine Modelle mit deinen Daten und teilt nichts mit Dritten. Diese Verarbeitung erfolgt innerhalb unserer gesicherten Infrastruktur und dient ausschliesslich dazu, dir zu helfen, deine Impfungen schneller und genauer zu erfassen.

## 5. Servicenachrichten und Newsletter

Beim Erstellen eines Kontos stimmst du dem Erhalt notwendiger Nachrichten zu, die fuer den Betrieb der App wesentlich sind, wie Sicherheitsmeldungen und Updates. Diese Kommunikation ist Teil der Vertragserfuellung und kann nicht deaktiviert werden, solange das Konto aktiv ist.

Wir duerfen dir per E-Mail Einladungen zu freiwilligen Umfragen ueber die App senden, damit wir den Dienst verbessern koennen. Die Teilnahme ist freiwillig. Diese Nachrichten beruhen auf unserem berechtigten Interesse (Artikel 6 Absatz 1 Buchstabe f DSGVO) und sind keine Werbung. Du kannst dich jederzeit ueber den Abmeldelink in der E-Mail abmelden. Antworten werden nicht mit Gesundheitsdaten aus deinem Konto verknuepft, es sei denn, du gibst diese selbst ein.

Zusaetzlich kannst du dich freiwillig fuer unseren Newsletter anmelden. Dieser enthaelt Neuigkeiten und Updates zu unseren Diensten. Du kannst dich jederzeit ueber den Abmeldelink oder in der App unter Benachrichtigungseinstellungen abmelden. Werbliche Nachrichten werden nur mit deiner vorherigen Einwilligung versendet.

## 6. Anonymisierte Forschung und Datennutzung

Vaxy kann anonymisierte oder aggregierte Daten fuer statistische oder wissenschaftliche Forschung verwenden, etwa um Einblicke in Impfverhalten oder Trends in der Praeventivversorgung zu gewinnen. Diese Daten sind nicht auf einzelne Personen zurueckfuehrbar. Ergebnisse werden nur in aggregierter oder vollstaendig anonymer Form praesentiert. Die Verarbeitung beruht auf dem berechtigten Interesse von Vaxy, seine Dienste zu verbessern und zu allgemeiner Forschung beizutragen, ohne die Privatsphaere zu verletzen.

## 7. Auftragsverarbeiter und Unterauftragsverarbeiter

Vaxy nutzt sorgfaeltig ausgewaehlte Auftragsverarbeiter und Unterauftragsverarbeiter (z. B. Hosting-, E-Mail- und Analysetools). Mit jeder Partei wurde ein Auftragsverarbeitungsvertrag geschlossen, der Artikel 28 DSGVO entspricht. Diese Parteien verarbeiten Daten ausschliesslich im Auftrag von Vaxy und unter dessen Aufsicht. Eine aktuelle Liste der Auftragsverarbeiter ist auf Anfrage ueber info@vaccinatieboekje.com verfuegbar.

## 8. Ort der Datenspeicherung

Personenbezogene Daten werden grundsaetzlich innerhalb des Europaeischen Wirtschaftsraums (EWR) verarbeitet. Findet eine Verarbeitung ausserhalb des EWR statt, erfolgt sie ausschliesslich mit geeigneten Garantien, wie EU-Standardvertragsklauseln (SCCs) oder einem Angemessenheitsbeschluss der Europaeischen Kommission.

## 9. Externe Plattformen (App Stores)

Die App wird ueber die App Stores von Apple und Google verbreitet. Vaxy hat keinen Einfluss auf die Datenverarbeitung durch diese Parteien. Bitte lies daher auch deren eigene Datenschutzerklaerungen. Vaxy uebernimmt keine Haftung fuer Schaeden, die aus der Nutzung dieser externen Dienste entstehen oder damit zusammenhaengen.

## 10. Cookies

Die App und Website verwenden Cookies, um die Funktionalitaet zu verbessern und Statistiken zu fuehren. Bei der ersten Nutzung wird fuer nicht notwendige Cookies eine Einwilligung eingeholt. Du kannst Cookies ueber deine Browsereinstellungen deaktivieren, jedoch funktionieren dann manche Funktionen moeglicherweise nicht richtig.

## 11. Analyse- und Messdienste

### 11.1 Google Analytics

Vaxy nutzt Google Analytics, um anonymisierte Informationen ueber die Nutzung der App zu sammeln. Mit Google wurde ein Auftragsverarbeitungsvertrag geschlossen. IP-Adressen werden anonymisiert und Daten werden nicht fuer andere Google-Dienste genutzt.

### 11.2 Mixpanel Analytics

Vaxy nutzt Mixpanel, um Einblick in die Nutzung der App zu erhalten. Dabei werden Geraete-IDs und Nutzungsstatistiken verarbeitet. Mixpanel anonymisiert IP-Adressen und darf die Daten nicht fuer eigene Zwecke verwenden. Weitere Informationen: mixpanel.com/legal/privacy-policy.

## 12. Datensicherheit

Vaxy trifft geeignete technische und organisatorische Massnahmen zum Schutz personenbezogener Daten, darunter:

- Verschluesselte Speicherung von Daten;
- Sichere Verbindungen (TLS/SSL);
- Interne Zugriffsbeschraenkungen und Protokollierung;
- Regelmaessige Sicherheitskontrollen.

Trotz dieser Massnahmen kann Vaxy keine absolute Sicherheit garantieren. Die Nutzung der App erfolgt auf eigenes Risiko. Vaxy haftet nicht fuer Schaeden infolge unbefugten Zugriffs oder Datenverlusts, es sei denn, es liegt Vorsatz oder grobe Fahrlaessigkeit unsererseits vor.

## 13. Aufbewahrungsfristen

Personenbezogene Daten werden nicht laenger aufbewahrt, als fuer den Zweck ihrer Erhebung erforderlich ist, es sei denn, eine gesetzliche Pflicht verlangt eine laengere Aufbewahrung. Anonymisierte Daten koennen fuer statistische Zwecke laenger gespeichert werden.

## 14. Aenderungen

Vaxy behaelt sich das Recht vor, diese Datenschutzerklaerung zu aendern. Die neueste Version ist stets in der App und auf der Website verfuegbar. Bei wesentlichen Aenderungen wirst du informiert.

## 15. Deine Rechte

Du hast das Recht:

- deine personenbezogenen Daten einzusehen;
- Unrichtigkeiten berichtigen zu lassen;
- deine Daten loeschen zu lassen;
- der Verarbeitung oder Direktwerbung zu widersprechen;
- deine Daten an einen Dritten uebertragen zu lassen;
- die Verarbeitung voruebergehend einschraenken zu lassen.

Anfragen koennen ueber info@vaccinatieboekje.com eingereicht werden. Vaxy kann um Identifikation bitten (mit verdeckter BSN und verdecktem Passfoto). Wir antworten innerhalb von 30 Tagen, sofern die Komplexitaet der Anfrage keine Verlaengerung erfordert.

## 16. Medizinischer Haftungsausschluss

Die Informationen in der Vaxy-App dienen ausschliesslich der Unterstuetzung der persoenlichen Gesundheitsverwaltung und stellen keine medizinische Beratung dar. Vaxy ist kein Gesundheitsdienstleister. Wende dich bei medizinischen Fragen immer an einen Arzt oder eine befugte medizinische Fachkraft. Vaxy uebernimmt keine Haftung fuer Schaeden, die aus der Nutzung der bereitgestellten Informationen entstehen.

## 17. DPIA und Verantwortlichkeiten

Vaxy hat fuer die Kernfunktionen der App eine Datenschutz-Folgenabschaetzung (DPIA) durchgefuehrt. Vaccinatieboekje B.V. tritt als Verantwortlicher fuer die ueber die App verarbeiteten Daten auf. Externe Partner oder Gesundheitsdienstleister, die ueber Schnittstellen Daten erhalten oder bereitstellen, sind selbststaendig fuer die Einhaltung der DSGVO bei ihren eigenen Verarbeitungen verantwortlich.

## 18. Beschwerden

Wenn du der Meinung bist, dass Vaxy nicht im Einklang mit dem Datenschutzrecht handelt, kannst du eine Beschwerde bei der niederlaendischen Datenschutzbehoerde (Autoriteit Persoonsgegevens) einreichen.

## 19. Haftung und anwendbares Recht

Vaxy bemueht sich, richtige und aktuelle Informationen bereitzustellen, garantiert jedoch nicht, dass die App jederzeit fehlerfrei oder ununterbrochen funktioniert. Die Nutzung der App erfolgt auf eigenes Risiko. Vaxy kann nicht fuer direkte oder indirekte Schaeden haftbar gemacht werden, die aus der Nutzung der App oder der angezeigten Informationen entstehen, ausser bei Vorsatz oder grober Fahrlaessigkeit.

Auf diese Datenschutzerklaerung findet ausschliesslich niederlaendisches Recht Anwendung. Streitigkeiten werden dem zustaendigen Gericht in Amsterdam vorgelegt.

## 20. Kontakt

Vaccinatieboekje B.V.  
Handelnd unter dem Namen Vaxy  
info@vaccinatieboekje.com  
vaxy.app | vaccinatieboekje.com`,
};

const privacyFr: LegalDoc = {
  title: "Declaration de confidentialite Vaxy",
  updated: "janvier 2025",
  markdown: `La presente declaration de confidentialite a ete etablie par Vaccinatieboekje B.V., exercant sous le nom Vaxy (ci-apres : "Vaxy" ou "nous"). Vaxy attache une grande importance a la protection des donnees personnelles et agit conformement au Reglement general sur la protection des donnees (RGPD) et aux autres lois et reglementations applicables.

Cette declaration s'applique a l'application mobile Vaxy et aux sites vaxy.app et vaccinatieboekje.com (ensemble : "l'application"). En utilisant l'application, tu acceptes la presente declaration de confidentialite.

## 1. Responsable du traitement et champ d'application

Vaccinatieboekje B.V., dont le siege statutaire est a Amsterdam, est le responsable du traitement des donnees personnelles via l'application. Cette declaration decrit comment Vaxy collecte, utilise, stocke et protege les donnees personnelles, ainsi que les droits des utilisateurs. Aucun droit ne peut etre tire de cette declaration.

## 2. Traitement des donnees et finalites

Vaxy ne traite que les donnees personnelles necessaires au fonctionnement de l'application, notamment :

- Donnees d'identification : nom, date de naissance, sexe, adresse e-mail et photo de profil.
- Donnees de sante : informations de vaccination que tu ajoutes toi-meme (maladie, date, marque, numero de lot, duree de validite).
- Donnees techniques : informations sur l'appareil et statistiques d'utilisation necessaires a la fonctionnalite et a la securite.

Ces donnees sont traitees pour :

- Creer et gerer ton compte.
- Afficher, conserver et mettre a jour ton apercu vaccinal.
- Envoyer les messages de service necessaires.
- Inviter a des enquetes volontaires sur l'application.
- Ameliorer la stabilite, la securite et la fiabilite de l'application.

Le traitement repose sur :

- Le consentement (article 6, paragraphe 1, point a, RGPD).
- L'execution du contrat (article 6, paragraphe 1, point b, RGPD).
- L'interet legitime a l'amelioration du service (article 6, paragraphe 1, point f, RGPD).

Dans la mesure ou des donnees de sante sont traitees, cela se fait exclusivement avec un consentement explicite au sens de l'article 9, paragraphe 2, point a, RGPD. Tu peux retirer ce consentement a tout moment via les parametres de ton compte.

## 3. Utilisation des photos et de l'appareil photo

L'application peut demander l'acces a tes photos ou a ton appareil photo afin d'ajouter ou de photographier des justificatifs de vaccination. Cet acces est facultatif et n'est accorde qu'apres ton consentement explicite. Les photos restent dans ton compte et ne sont pas partagees avec des tiers, sauf si tu choisis de le faire.

## 4. Reconnaissance automatique par intelligence artificielle (IA)

Pour faciliter la saisie des vaccinations, Vaxy utilise l'intelligence artificielle (IA) afin de reconnaitre automatiquement les justificatifs de vaccination. L'IA ne traite que le texte visible sur le justificatif, n'utilise pas la reconnaissance faciale ni d'autres informations personnelles, n'entraine pas de modeles avec tes donnees et ne partage rien avec des tiers. Ce traitement a lieu dans notre infrastructure securisee et vise uniquement a t'aider a enregistrer tes vaccinations plus rapidement et plus precisement.

## 5. Messages de service et newsletters

Lors de la creation d'un compte, tu acceptes de recevoir les messages necessaires au fonctionnement de l'application, tels que les avis de securite et les mises a jour. Cette communication fait partie de l'execution du contrat et ne peut pas etre desactivee tant que le compte est actif.

Nous pouvons t'envoyer par e-mail des invitations a des enquetes volontaires sur l'application, afin d'ameliorer le service. La participation est volontaire. Ces messages reposent sur notre interet legitime (article 6, paragraphe 1, point f, RGPD) et ne sont pas des messages promotionnels. Tu peux te desinscrire a tout moment via le lien de desinscription dans l'e-mail. Les reponses ne sont pas liees aux donnees de sante de ton compte, sauf si tu les saisis toi-meme.

Tu peux egalement t'inscrire volontairement a notre newsletter. Elle contient des actualites et des mises a jour sur nos services. Tu peux te desinscrire a tout moment via le lien de desinscription ou dans l'application, sous les parametres de notification. Les messages promotionnels ne sont envoyes qu'avec ton consentement prealable.

## 6. Recherche anonymisee et utilisation des donnees

Vaxy peut utiliser des donnees anonymisees ou agregees a des fins de recherche statistique ou scientifique, par exemple pour comprendre les comportements vaccinaux ou les tendances de soins preventifs. Ces donnees ne permettent pas d'identifier des personnes individuelles. Les resultats ne sont presentes que sous forme agregee ou totalement anonyme. Le traitement repose sur l'interet legitime de Vaxy a ameliorer ses services et a contribuer a la recherche generale sans porter atteinte a la vie privee.

## 7. Sous-traitants et sous-traitants ulterieurs

Vaxy utilise des sous-traitants et sous-traitants ulterieurs selectionnes avec soin (tels que des outils d'hebergement, d'e-mail et d'analyse). Un accord de traitement conforme a l'article 28 du RGPD a ete conclu avec chaque partie. Ces parties traitent les donnees uniquement pour le compte de Vaxy et sous sa supervision. Une liste actualisee des sous-traitants est disponible sur demande via info@vaccinatieboekje.com.

## 8. Lieu de stockage des donnees

Les donnees personnelles sont en principe traitees dans l'Espace economique europeen (EEE). Si un traitement a lieu en dehors de l'EEE, il est effectue uniquement avec des garanties appropriees, telles que les clauses contractuelles types de l'UE (CCT) ou une decision d'adequation de la Commission europeenne.

## 9. Plateformes externes (App Stores)

L'application est distribuee via les boutiques d'applications d'Apple et de Google. Vaxy n'a aucune influence sur le traitement des donnees par ces parties. Lis donc egalement leurs propres declarations de confidentialite. Vaxy decline toute responsabilite pour les dommages decoulant de l'utilisation de ces services externes ou y etant lies.

## 10. Cookies

L'application et le site utilisent des cookies pour ameliorer la fonctionnalite et tenir des statistiques. Lors de la premiere utilisation, le consentement est demande pour les cookies non essentiels. Tu peux desactiver les cookies via les parametres de ton navigateur, mais certaines fonctions peuvent alors ne pas fonctionner correctement.

## 11. Services d'analyse et de mesure

### 11.1 Google Analytics

Vaxy utilise Google Analytics pour collecter des informations anonymisees sur l'utilisation de l'application. Un accord de traitement a ete conclu avec Google. Les adresses IP sont anonymisees et les donnees ne sont pas utilisees pour d'autres services Google.

### 11.2 Mixpanel Analytics

Vaxy utilise Mixpanel pour comprendre l'utilisation de l'application. Des identifiants d'appareil et des statistiques d'utilisation sont traites a cette fin. Mixpanel anonymise les adresses IP et ne peut pas utiliser les donnees pour ses propres finalites. Plus d'informations : mixpanel.com/legal/privacy-policy.

## 12. Securite des donnees

Vaxy prend des mesures techniques et organisationnelles appropriees pour proteger les donnees personnelles, notamment :

- Stockage chiffre des donnees ;
- Connexions securisees (TLS/SSL) ;
- Restrictions d'acces internes et journalisation ;
- Controles de securite reguliers.

Malgre ces mesures, Vaxy ne peut garantir une securite absolue. L'utilisation de l'application se fait a tes propres risques. Vaxy n'est pas responsable des dommages resultant d'un acces non autorise ou d'une perte de donnees, sauf en cas d'intention ou de negligence grave de sa part.

## 13. Durees de conservation

Les donnees personnelles ne sont pas conservees plus longtemps que necessaire pour la finalite pour laquelle elles ont ete collectees, sauf si une obligation legale impose une duree de conservation plus longue. Les donnees anonymisees peuvent etre conservees plus longtemps a des fins statistiques.

## 14. Modifications

Vaxy se reserve le droit de modifier cette declaration de confidentialite. La version la plus recente est toujours disponible dans l'application et sur le site. En cas de modifications importantes, tu en seras informe.

## 15. Tes droits

Tu as le droit de :

- consulter tes donnees personnelles ;
- faire corriger les inexactitudes ;
- faire supprimer tes donnees ;
- t'opposer au traitement ou au marketing direct ;
- faire transferer tes donnees a un tiers ;
- faire limiter temporairement le traitement.

Les demandes peuvent etre soumises via info@vaccinatieboekje.com. Vaxy peut demander une identification (avec BSN et photo de passeport masques). Nous repondons dans un delai de 30 jours, sauf si la complexite de la demande necessite une prolongation.

## 16. Avertissement medical

Les informations de l'application Vaxy sont destinees uniquement a soutenir l'administration personnelle de la sante et ne constituent pas un avis medical. Vaxy n'est pas un prestataire de soins. Consulte toujours un medecin ou un professionnel de sante qualifie pour toute question medicale. Vaxy decline toute responsabilite pour les dommages decoulant de l'utilisation des informations fournies.

## 17. DPIA et responsabilites

Vaxy a realise une analyse d'impact relative a la protection des donnees (DPIA) pour les fonctionnalites principales de l'application. Vaccinatieboekje B.V. agit en tant que responsable du traitement pour les donnees traitees via l'application. Les partenaires externes ou prestataires de soins qui recoivent ou fournissent des donnees via des integrations sont eux-memes responsables du respect du RGPD pour leurs propres traitements.

## 18. Plaintes

Si tu estimes que Vaxy n'agit pas conformement a la legislation sur la protection de la vie privee, tu peux deposer une plainte aupres de l'autorite neerlandaise de protection des donnees (Autoriteit Persoonsgegevens).

## 19. Responsabilite et droit applicable

Vaxy s'efforce de fournir des informations exactes et a jour, mais ne garantit pas que l'application fonctionnera toujours sans erreur ou sans interruption. L'utilisation de l'application se fait a tes propres risques. Vaxy ne peut etre tenue responsable des dommages directs ou indirects decoulant de l'utilisation de l'application ou des informations affichees, sauf en cas d'intention ou de negligence grave.

La presente declaration de confidentialite est exclusivement regie par le droit neerlandais. Les litiges sont soumis au tribunal competent d'Amsterdam.

## 20. Contact

Vaccinatieboekje B.V.  
Exercant sous le nom Vaxy  
info@vaccinatieboekje.com  
vaxy.app | vaccinatieboekje.com`,
};

const privacyEs: LegalDoc = {
  title: "Declaracion de privacidad de Vaxy",
  updated: "enero de 2025",
  markdown: `Esta declaracion de privacidad ha sido elaborada por Vaccinatieboekje B.V., que opera bajo el nombre Vaxy (en adelante: "Vaxy" o "nosotros"). Vaxy concede gran importancia a la proteccion de los datos personales y actua de conformidad con el Reglamento General de Proteccion de Datos (RGPD) y demas leyes y normas aplicables.

Esta declaracion se aplica a la aplicacion movil Vaxy y a los sitios web vaxy.app y vaccinatieboekje.com (conjuntamente: "la aplicacion"). Al utilizar la aplicacion, aceptas esta declaracion de privacidad.

## 1. Responsable del tratamiento y ambito de aplicacion

Vaccinatieboekje B.V., con domicilio estatutario en Amsterdam, es el responsable del tratamiento de los datos personales a traves de la aplicacion. Esta declaracion describe como Vaxy recopila, utiliza, almacena y protege los datos personales, asi como los derechos de los usuarios. De esta declaracion no pueden derivarse derechos.

## 2. Tratamiento de datos y finalidades

Vaxy solo trata los datos personales necesarios para el funcionamiento de la aplicacion, incluidos:

- Datos de identificacion: nombre, fecha de nacimiento, sexo, direccion de correo electronico y foto de perfil.
- Datos de salud: informacion de vacunacion que anades tu mismo (como enfermedad, fecha, marca, numero de lote y periodo de validez).
- Datos tecnicos: informacion del dispositivo y estadisticas de uso necesarias para la funcionalidad y la seguridad.

Estos datos se tratan para:

- Crear y gestionar tu cuenta.
- Mostrar, conservar y actualizar tu resumen de vacunacion.
- Enviar mensajes de servicio necesarios.
- Invitar a encuestas voluntarias sobre la aplicacion.
- Mejorar la estabilidad, seguridad y fiabilidad de la aplicacion.

El tratamiento se basa en:

- Consentimiento (articulo 6, apartado 1, letra a, RGPD).
- Ejecucion del contrato (articulo 6, apartado 1, letra b, RGPD).
- Interes legitimo en mejorar el servicio (articulo 6, apartado 1, letra f, RGPD).

En la medida en que se traten datos de salud, esto se hace exclusivamente con consentimiento explicito conforme al articulo 9, apartado 2, letra a, RGPD. Puedes retirar este consentimiento en cualquier momento desde la configuracion de tu cuenta.

## 3. Uso de fotos y camara

La aplicacion puede solicitar acceso a tus fotos o camara para anadir o fotografiar justificantes de vacunacion. Este acceso es opcional y solo se concede tras tu consentimiento explicito. Las fotos permanecen en tu cuenta y no se comparten con terceros salvo que tu mismo lo decidas.

## 4. Reconocimiento automatico con inteligencia artificial (IA)

Para facilitar la introduccion de vacunas, Vaxy utiliza inteligencia artificial (IA) para reconocer automaticamente justificantes de vacunacion. La IA solo trata el texto visible en el justificante, no utiliza reconocimiento facial ni otra informacion personal, no entrena modelos con tus datos y no comparte nada con terceros. Este tratamiento se realiza dentro de nuestra infraestructura segura y tiene como unico objetivo ayudarte a registrar tus vacunas con mayor rapidez y precision.

## 5. Mensajes de servicio y boletines

Al crear una cuenta, aceptas recibir mensajes necesarios que son esenciales para el funcionamiento de la aplicacion, como avisos de seguridad y actualizaciones. Esta comunicacion forma parte de la ejecucion del contrato y no puede desactivarse mientras la cuenta este activa.

Podemos enviarte por correo electronico invitaciones a encuestas voluntarias sobre la aplicacion, para mejorar el servicio. La participacion es voluntaria. Estos mensajes se basan en nuestro interes legitimo (articulo 6, apartado 1, letra f, RGPD) y no son mensajes promocionales. Puedes darte de baja en cualquier momento mediante el enlace de baja del correo. Las respuestas no se vinculan a datos de salud de tu cuenta, salvo que tu mismo los indiques.

Tambien puedes suscribirte voluntariamente a nuestro boletin. Contiene noticias y actualizaciones sobre nuestros servicios. Puedes darte de baja en cualquier momento mediante el enlace de baja o en la aplicacion, en la configuracion de notificaciones. Los mensajes promocionales solo se envian con tu consentimiento previo.

## 6. Investigacion anonimizada y uso de datos

Vaxy puede utilizar datos anonimizados o agregados para investigacion estadistica o cientifica, por ejemplo para obtener informacion sobre comportamientos de vacunacion o tendencias en atencion preventiva. Estos datos no pueden vincularse a personas individuales. Los resultados solo se presentan de forma agregada o completamente anonima. El tratamiento se basa en el interes legitimo de Vaxy en mejorar sus servicios y contribuir a la investigacion general sin vulnerar la privacidad.

## 7. Encargados y subencargados del tratamiento

Vaxy utiliza encargados y subencargados cuidadosamente seleccionados (como herramientas de alojamiento, correo electronico y analitica). Con cada parte se ha celebrado un acuerdo de tratamiento conforme al articulo 28 del RGPD. Estas partes tratan datos exclusivamente por encargo de Vaxy y bajo su supervision. Una lista actualizada de encargados esta disponible previa solicitud en info@vaccinatieboekje.com.

## 8. Lugar de almacenamiento de datos

Los datos personales se tratan en principio dentro del Espacio Economico Europeo (EEE). Si el tratamiento tiene lugar fuera del EEE, se realiza exclusivamente con garantias adecuadas, como clausulas contractuales tipo de la UE (SCC) o una decision de adecuacion de la Comision Europea.

## 9. Plataformas externas (App Stores)

La aplicacion se distribuye a traves de las tiendas de aplicaciones de Apple y Google. Vaxy no influye en el tratamiento de datos por parte de estas entidades. Lee tambien sus propias declaraciones de privacidad. Vaxy no acepta responsabilidad por danos derivados del uso de estos servicios externos o relacionados con el mismo.

## 10. Cookies

La aplicacion y el sitio web utilizan cookies para mejorar la funcionalidad y mantener estadisticas. En el primer uso se solicita consentimiento para las cookies no esenciales. Puedes desactivar las cookies desde la configuracion de tu navegador, pero algunas funciones podrian no funcionar correctamente.

## 11. Servicios de analitica y medicion

### 11.1 Google Analytics

Vaxy utiliza Google Analytics para recopilar informacion anonimizada sobre el uso de la aplicacion. Se ha celebrado un acuerdo de tratamiento con Google. Las direcciones IP se anonimizan y los datos no se utilizan para otros servicios de Google.

### 11.2 Mixpanel Analytics

Vaxy utiliza Mixpanel para comprender el uso de la aplicacion. Para ello se tratan identificadores de dispositivo y estadisticas de uso. Mixpanel anonimiza las direcciones IP y no puede utilizar los datos para sus propios fines. Mas informacion: mixpanel.com/legal/privacy-policy.

## 12. Seguridad de los datos

Vaxy adopta medidas tecnicas y organizativas adecuadas para proteger los datos personales, incluidas:

- Almacenamiento cifrado de datos;
- Conexiones seguras (TLS/SSL);
- Restricciones internas de acceso y registro;
- Controles de seguridad periodicos.

A pesar de estas medidas, Vaxy no puede garantizar una seguridad absoluta. El uso de la aplicacion corre por tu cuenta y riesgo. Vaxy no es responsable de danos resultantes de acceso no autorizado o perdida de datos, salvo en caso de dolo o negligencia grave por su parte.

## 13. Plazos de conservacion

Los datos personales no se conservan durante mas tiempo del necesario para la finalidad para la que fueron recopilados, salvo que una obligacion legal exija un periodo de conservacion mas largo. Los datos anonimizados pueden conservarse durante mas tiempo con fines estadisticos.

## 14. Cambios

Vaxy se reserva el derecho de modificar esta declaracion de privacidad. La version mas reciente esta siempre disponible en la aplicacion y en el sitio web. Se te informara de los cambios importantes.

## 15. Tus derechos

Tienes derecho a:

- acceder a tus datos personales;
- corregir inexactitudes;
- eliminar tus datos;
- oponerte al tratamiento o al marketing directo;
- transferir tus datos a un tercero;
- limitar temporalmente el tratamiento.

Las solicitudes pueden enviarse a info@vaccinatieboekje.com. Vaxy puede pedir identificacion (con BSN y foto de pasaporte ocultos). Respondemos en un plazo de 30 dias, salvo que la complejidad de la solicitud requiera una prorroga.

## 16. Aviso medico

La informacion de la aplicacion Vaxy esta destinada exclusivamente a apoyar la administracion personal de la salud y no constituye asesoramiento medico. Vaxy no es un proveedor sanitario. Consulta siempre a un medico o profesional sanitario cualificado para cuestiones medicas. Vaxy no acepta responsabilidad por danos derivados del uso de la informacion proporcionada.

## 17. DPIA y responsabilidades

Vaxy ha realizado una evaluacion de impacto relativa a la proteccion de datos (DPIA) para las funcionalidades principales de la aplicacion. Vaccinatieboekje B.V. actua como responsable del tratamiento de los datos tratados a traves de la aplicacion. Los socios externos o proveedores sanitarios que reciban o proporcionen datos mediante integraciones son responsables independientes del cumplimiento del RGPD para sus propios tratamientos.

## 18. Reclamaciones

Si consideras que Vaxy no actua de conformidad con la legislacion de privacidad, puedes presentar una reclamacion ante la autoridad neerlandesa de proteccion de datos (Autoriteit Persoonsgegevens).

## 19. Responsabilidad y derecho aplicable

Vaxy se esfuerza por proporcionar informacion correcta y actualizada, pero no garantiza que la aplicacion funcione siempre sin errores o sin interrupciones. El uso de la aplicacion corre por tu cuenta y riesgo. Vaxy no puede ser considerada responsable de danos directos o indirectos derivados del uso de la aplicacion o de la informacion mostrada, salvo en caso de dolo o negligencia grave.

Esta declaracion de privacidad se rige exclusivamente por el derecho neerlandes. Las disputas se someteran al tribunal competente de Amsterdam.

## 20. Contacto

Vaccinatieboekje B.V.  
Opera bajo el nombre Vaxy  
info@vaccinatieboekje.com  
vaxy.app | vaccinatieboekje.com`,
};

const cookiesNl: LegalDoc = {
  title: "Cookieverklaring",
  updated: "18 september 2025",
  markdown: `Vaxy is een app en handelsnaam van Vaccinatieboekje B.V.

Deze cookieverklaring legt uit hoe Vaccinatieboekje B.V. ("Vaxy", "wij", "ons") cookies en vergelijkbare technologieen (zoals pixels, SDK's en local storage) gebruikt op onze website(s) en webapplicatie(s) (gezamenlijk: de "Website"). Voor de verwerking van persoonsgegevens verwijzen we naar onze Privacy- en cookieverklaring.

---

## 1. Wat zijn cookies?

Cookies zijn kleine tekstbestanden die bij een bezoek aan de Website op je apparaat worden geplaatst. Onder "cookies" verstaan we ook vergelijkbare technieken zoals tracking pixels, tags, SDK's en local storage. Sommige cookies zijn noodzakelijk voor de werking van de Website, andere helpen ons om prestaties te meten, functionaliteit te verbeteren of relevantere advertenties te tonen.

---

## 2. Toestemming & je keuzes

Bij je eerste bezoek tonen wij een cookiebanner met uitleg. Daar kun je toestemming geven voor niet-essentiele cookies (bijv. statistiek/advertentie) of je voorkeuren instellen. Je kunt je keuze op elk moment wijzigen of intrekken via:

Cookievoorkeuren beheren

We plaatsen altijd strikt noodzakelijke cookies (geen toestemming vereist). Voor statistiek (anoniem/aggregaat) gebruiken we bij voorkeur privacyvriendelijke instellingen; voor alle vormen van (re)targeting of cross-site tracking vragen we vooraf toestemming.

---

## 3. Welke categorieen gebruiken wij?

### 3.1. Strikt noodzakelijke cookies (altijd aan)

Nodig voor basisfuncties zoals paginanavigatie, beveiliging (bijv. CSRF), load balancing en het onthouden van je cookiekeuze. Zonder deze cookies werkt de Website niet goed. De cookie \`vaxy_locale\` onthoudt je taalvoorkeur en vereist geen toestemming.

### 3.2. Voorkeuren / Functioneel (optioneel)

Onthouden van instellingen (bijv. taal, regio) en verbeterde functies zoals inloggen of het tonen van embedded content.

### 3.3. Statistiek / Analytics (optioneel)

Helpt ons te begrijpen hoe de Website wordt gebruikt (bezochte pagina's, sessieduur, foutmeldingen) zodat we kunnen verbeteren. We gebruiken waar mogelijk privacyvriendelijke, geanonimiseerde metingen. Voor identificeerbare tracking vragen we toestemming.

### 3.4. Marketing / Advertenties (optioneel)

Gebruikt voor (re)targeting, het opbouwen van doelgroepen en het meten van advertentiecampagnes op platforms zoals Facebook/Instagram en LinkedIn. Alleen met jouw expliciete toestemming.

---

## 4. Overzicht (voorbeeld) cookies & pixels

De specifieke cookies kunnen wijzigen per update of campagne. Hieronder een voorbeeldoverzicht van veelgebruikte cookies en pixeltechnologieen die wij kunnen inzetten. De actuele status en granulariteit vind je in de cookiebanner / voorkeurenmanager.

| Dienst | Doel / Categorie | Type / Voorbeeldnaam | Bewaartermijn | Provider / Domein |
| --- | --- | --- | --- | --- |
| First-party (Vaxy) | Noodzakelijk (cookiekeuze, sessie, CSRF) | cookie_consent, session_id, csrftoken | Sessie t/m 12 mnd | vaxy.app / *.vaxy.app, *.vaccinatieboekje.com |
| Analytics (Google Analytics 4) | Statistiek | _ga, _ga_*, _gid (privacyvriendelijk ingesteld) | 24 uur t/m 26 mnd | vaxy.app / google.com |
| Meta Pixel (Facebook/Instagram) | Marketing / (Re)targeting | _fbp + pixel events | 90 dagen (typisch) | vaxy.app / facebook.com |

Let op: oudere Universal Analytics cookies (zoals __utma, __utmz) kunnen nog voorkomen bij historische implementaties, maar we migreren naar GA4. Raadpleeg altijd de cookiebanner voor de actuele lijst.

---

## 5. Google Analytics (privacyvriendelijk)

Wij gebruiken Google Analytics (bij voorkeur GA4) om geaggregeerde statistieken te verzamelen. Wij hebben privacyvriendelijke instellingen geactiveerd (zoals IP-maskering) en een verwerkersovereenkomst gesloten met Google. Data wordt niet gedeeld met andere Google-diensten voor eigen doeleinden van Google. Waar mogelijk beperken wij bewaartermijnen (bijv. <= 26 maanden).

---

## 6. Advertenties (Meta/LinkedIn) - alleen na toestemming

Met jouw toestemming kunnen wij advertentiecookies en pixels gebruiken van o.a. Meta (Facebook/Instagram) en LinkedIn om doelgroepen op te bouwen, campagnes te optimaliseren en resultaten te meten (retargeting). Je kunt je toestemming altijd intrekken via Cookievoorkeuren beheren. Zonder toestemming plaatsen we deze cookies niet.

---

## 7. Internationale doorgiften

Sommige providers (zoals Google, Meta, LinkedIn) kunnen gegevens buiten de Europese Economische Ruimte verwerken. Waar relevant treffen wij passende waarborgen (bijv. door modelcontractbepalingen) en beperken wij datastromen tot het noodzakelijke. Zie ook hun privacyverklaringen voor details.

---

## 8. Cookies in- en uitschakelen (browser)

Je kunt cookies ook beheren via je browserinstellingen. Houd er rekening mee dat het uitschakelen van alle cookies de functionaliteit van (delen van) de Website kan beperken. Raadpleeg de helpsectie van je browser voor instructies.

---

## 9. Bewaartermijnen & verwijderen

De meeste cookies verlopen automatisch na een bepaalde periode ("verlooptijd"). Je kunt cookies ook handmatig verwijderen via je browser. Voor statistieken en logs hanteren wij zo kort mogelijke bewaartermijnen die passen bij het doel (zie de Privacyverklaring).

---

## 10. Websites van derden

Deze cookieverklaring is niet van toepassing op websites of diensten van derden waarnaar wij linken. Wij raden je aan de privacy- en cookieverklaringen van deze partijen te lezen.

---

## 11. Wijzigingen

Wij kunnen deze cookieverklaring van tijd tot tijd aanpassen, bijvoorbeeld bij wijzigingen aan onze Website of cookie-technologie. Controleer daarom regelmatig de datum "Laatst bijgewerkt" bovenaan.

---

## 12. Contact

Vaccinatieboekje B.V.  
Gevestigd te Amsterdam  
E-mail: info@vaccinatieboekje.com`,
};

const cookiesEn: LegalDoc = {
  title: "Cookie Policy",
  updated: "18 September 2025",
  markdown: `Vaxy is an app and trade name of Vaccinatieboekje B.V.

This cookie policy explains how Vaccinatieboekje B.V. ("Vaxy", "we", "us") uses cookies and similar technologies (such as pixels, SDKs and local storage) on our website(s) and web application(s) (together: the "Website"). For the processing of personal data, please see our Privacy and cookie statement.

---

## 1. What are cookies?

Cookies are small text files placed on your device when you visit the Website. By "cookies" we also mean comparable techniques such as tracking pixels, tags, SDKs and local storage. Some cookies are necessary for the Website to work; others help us measure performance, improve functionality or show more relevant advertisements.

---

## 2. Consent & your choices

On your first visit we show a cookie banner with an explanation. There you can consent to non-essential cookies (for example statistics/advertising) or set your preferences. You can change or withdraw your choice at any time via:

Manage cookie preferences

We always place strictly necessary cookies (no consent required). For statistics (anonymous/aggregate) we prefer privacy-friendly settings; for all forms of (re)targeting or cross-site tracking we ask for prior consent.

---

## 3. Which categories do we use?

### 3.1. Strictly necessary cookies (always on)

Required for basic functions such as page navigation, security (for example CSRF), load balancing and remembering your cookie choice. Without these cookies the Website does not work properly. The \`vaxy_locale\` cookie remembers your language preference and does not require consent.

### 3.2. Preferences / Functional (optional)

Remembering settings (for example language, region) and enhanced functions such as login or showing embedded content.

### 3.3. Statistics / Analytics (optional)

Helps us understand how the Website is used (visited pages, session duration, error messages) so we can improve it. Where possible, we use privacy-friendly, anonymized measurements. We ask for consent for identifiable tracking.

### 3.4. Marketing / Advertising (optional)

Used for (re)targeting, building audiences and measuring advertising campaigns on platforms such as Facebook/Instagram and LinkedIn. Only with your explicit consent.

---

## 4. Overview of example cookies & pixels

The specific cookies may change per update or campaign. Below is an example overview of commonly used cookies and pixel technologies we may use. The current status and granularity can be found in the cookie banner / preference manager.

| Service | Purpose / Category | Type / Example name | Retention period | Provider / Domain |
| --- | --- | --- | --- | --- |
| First-party (Vaxy) | Necessary (cookie choice, session, CSRF) | cookie_consent, session_id, csrftoken | Session up to 12 months | vaxy.app / *.vaxy.app, *.vaccinatieboekje.com |
| Analytics (Google Analytics 4) | Statistics | _ga, _ga_*, _gid (privacy-friendly settings) | 24 hours up to 26 months | vaxy.app / google.com |
| Meta Pixel (Facebook/Instagram) | Marketing / (Re)targeting | _fbp + pixel events | 90 days (typical) | vaxy.app / facebook.com |

Please note: older Universal Analytics cookies (such as __utma, __utmz) may still occur in historical implementations, but we are migrating to GA4. Always consult the cookie banner for the current list.

---

## 5. Google Analytics (privacy-friendly)

We use Google Analytics (preferably GA4) to collect aggregated statistics. We have activated privacy-friendly settings (such as IP masking) and concluded a processing agreement with Google. Data is not shared with other Google services for Google's own purposes. Where possible, we limit retention periods (for example <= 26 months).

---

## 6. Advertising (Meta/LinkedIn) - only after consent

With your consent we may use advertising cookies and pixels from Meta (Facebook/Instagram), LinkedIn and others to build audiences, optimize campaigns and measure results (retargeting). You can withdraw your consent at any time via Manage cookie preferences. Without consent, we do not place these cookies.

---

## 7. International transfers

Some providers (such as Google, Meta and LinkedIn) may process data outside the European Economic Area. Where relevant, we take appropriate safeguards (for example standard contractual clauses) and limit data flows to what is necessary. See also their privacy statements for details.

---

## 8. Enabling and disabling cookies (browser)

You can also manage cookies through your browser settings. Please note that disabling all cookies may limit the functionality of parts of the Website. Consult your browser's help section for instructions.

---

## 9. Retention periods & deletion

Most cookies expire automatically after a certain period ("expiry time"). You can also manually delete cookies through your browser. For statistics and logs we use retention periods that are as short as possible and appropriate for the purpose (see the Privacy Statement).

---

## 10. Third-party websites

This cookie policy does not apply to third-party websites or services to which we link. We recommend reading the privacy and cookie statements of those parties.

---

## 11. Changes

We may amend this cookie policy from time to time, for example due to changes to our Website or cookie technology. Please regularly check the "Last updated" date above.

---

## 12. Contact

Vaccinatieboekje B.V.  
Established in Amsterdam  
Email: info@vaccinatieboekje.com`,
};

const cookiesDe: LegalDoc = {
  title: "Cookie-Erklaerung",
  updated: "18. September 2025",
  markdown: `Vaxy ist eine App und ein Handelsname von Vaccinatieboekje B.V.

Diese Cookie-Erklaerung erlaeutert, wie Vaccinatieboekje B.V. ("Vaxy", "wir", "uns") Cookies und vergleichbare Technologien (wie Pixel, SDKs und Local Storage) auf unseren Websites und Webanwendungen (gemeinsam: die "Website") verwendet. Zur Verarbeitung personenbezogener Daten verweisen wir auf unsere Datenschutz- und Cookie-Erklaerung.

---

## 1. Was sind Cookies?

Cookies sind kleine Textdateien, die beim Besuch der Website auf deinem Geraet gespeichert werden. Unter "Cookies" verstehen wir auch vergleichbare Techniken wie Tracking-Pixel, Tags, SDKs und Local Storage. Einige Cookies sind fuer den Betrieb der Website notwendig, andere helfen uns, Leistung zu messen, Funktionen zu verbessern oder relevantere Werbung anzuzeigen.

---

## 2. Einwilligung & deine Auswahl

Bei deinem ersten Besuch zeigen wir ein Cookie-Banner mit Erlaeuterung. Dort kannst du nicht notwendigen Cookies (z. B. Statistik/Werbung) zustimmen oder deine Praeferenzen einstellen. Du kannst deine Auswahl jederzeit aendern oder widerrufen ueber:

Cookie-Praeferenzen verwalten

Wir setzen immer strikt notwendige Cookies (keine Einwilligung erforderlich). Fuer Statistik (anonym/aggregiert) verwenden wir vorzugsweise datenschutzfreundliche Einstellungen; fuer alle Formen von (Re-)Targeting oder Cross-Site-Tracking holen wir vorab Einwilligung ein.

---

## 3. Welche Kategorien verwenden wir?

### 3.1. Strikt notwendige Cookies (immer aktiv)

Erforderlich fuer Grundfunktionen wie Seitennavigation, Sicherheit (z. B. CSRF), Lastverteilung und das Speichern deiner Cookie-Auswahl. Ohne diese Cookies funktioniert die Website nicht richtig. Das Cookie \`vaxy_locale\` merkt sich deine Spracheinstellung und erfordert keine Einwilligung.

### 3.2. Praeferenzen / Funktional (optional)

Speichern von Einstellungen (z. B. Sprache, Region) und erweiterten Funktionen wie Anmeldung oder Anzeige eingebetteter Inhalte.

### 3.3. Statistik / Analytics (optional)

Hilft uns zu verstehen, wie die Website genutzt wird (besuchte Seiten, Sitzungsdauer, Fehlermeldungen), damit wir sie verbessern koennen. Wo moeglich verwenden wir datenschutzfreundliche, anonymisierte Messungen. Fuer identifizierbares Tracking holen wir Einwilligung ein.

### 3.4. Marketing / Werbung (optional)

Wird fuer (Re-)Targeting, Zielgruppenbildung und Messung von Werbekampagnen auf Plattformen wie Facebook/Instagram und LinkedIn verwendet. Nur mit deiner ausdruecklichen Einwilligung.

---

## 4. Uebersicht beispielhafter Cookies & Pixel

Die konkreten Cookies koennen sich je nach Update oder Kampagne aendern. Unten findest du eine beispielhafte Uebersicht haeufig genutzter Cookies und Pixel-Technologien, die wir einsetzen koennen. Den aktuellen Status und die Granularitaet findest du im Cookie-Banner / Praeferenzmanager.

| Dienst | Zweck / Kategorie | Typ / Beispielname | Aufbewahrungsfrist | Anbieter / Domain |
| --- | --- | --- | --- | --- |
| First-party (Vaxy) | Notwendig (Cookie-Auswahl, Sitzung, CSRF) | cookie_consent, session_id, csrftoken | Sitzung bis 12 Monate | vaxy.app / *.vaxy.app, *.vaccinatieboekje.com |
| Analytics (Google Analytics 4) | Statistik | _ga, _ga_*, _gid (datenschutzfreundlich eingestellt) | 24 Stunden bis 26 Monate | vaxy.app / google.com |
| Meta Pixel (Facebook/Instagram) | Marketing / (Re-)Targeting | _fbp + Pixel-Events | 90 Tage (typisch) | vaxy.app / facebook.com |

Hinweis: Aeltere Universal-Analytics-Cookies (wie __utma, __utmz) koennen bei historischen Implementierungen noch vorkommen, wir migrieren jedoch zu GA4. Konsultiere immer das Cookie-Banner fuer die aktuelle Liste.

---

## 5. Google Analytics (datenschutzfreundlich)

Wir verwenden Google Analytics (vorzugsweise GA4), um aggregierte Statistiken zu erheben. Wir haben datenschutzfreundliche Einstellungen aktiviert (wie IP-Maskierung) und einen Auftragsverarbeitungsvertrag mit Google geschlossen. Daten werden nicht mit anderen Google-Diensten fuer eigene Zwecke von Google geteilt. Wo moeglich begrenzen wir Aufbewahrungsfristen (z. B. <= 26 Monate).

---

## 6. Werbung (Meta/LinkedIn) - nur nach Einwilligung

Mit deiner Einwilligung koennen wir Werbe-Cookies und Pixel u. a. von Meta (Facebook/Instagram) und LinkedIn verwenden, um Zielgruppen aufzubauen, Kampagnen zu optimieren und Ergebnisse zu messen (Retargeting). Du kannst deine Einwilligung jederzeit ueber Cookie-Praeferenzen verwalten widerrufen. Ohne Einwilligung setzen wir diese Cookies nicht.

---

## 7. Internationale Uebermittlungen

Einige Anbieter (wie Google, Meta, LinkedIn) koennen Daten ausserhalb des Europaeischen Wirtschaftsraums verarbeiten. Wo relevant, treffen wir geeignete Garantien (z. B. durch Standardvertragsklauseln) und beschraenken Datenfluesse auf das Notwendige. Siehe auch deren Datenschutzerklaerungen fuer Details.

---

## 8. Cookies im Browser ein- und ausschalten

Du kannst Cookies auch ueber deine Browsereinstellungen verwalten. Beachte, dass das Deaktivieren aller Cookies die Funktionalitaet von Teilen der Website einschraenken kann. Konsultiere die Hilfeseiten deines Browsers fuer Anweisungen.

---

## 9. Aufbewahrungsfristen & Loeschen

Die meisten Cookies laufen nach einer bestimmten Zeit automatisch ab ("Ablaufzeit"). Du kannst Cookies auch manuell ueber deinen Browser loeschen. Fuer Statistiken und Logs verwenden wir moeglichst kurze Aufbewahrungsfristen, die zum Zweck passen (siehe Datenschutzerklaerung).

---

## 10. Websites Dritter

Diese Cookie-Erklaerung gilt nicht fuer Websites oder Dienste Dritter, auf die wir verlinken. Wir empfehlen, die Datenschutz- und Cookie-Erklaerungen dieser Parteien zu lesen.

---

## 11. Aenderungen

Wir koennen diese Cookie-Erklaerung von Zeit zu Zeit anpassen, etwa bei Aenderungen an unserer Website oder Cookie-Technologie. Pruefe daher regelmaessig das oben angegebene Datum "Zuletzt aktualisiert".

---

## 12. Kontakt

Vaccinatieboekje B.V.  
Sitz in Amsterdam  
E-Mail: info@vaccinatieboekje.com`,
};

const cookiesFr: LegalDoc = {
  title: "Politique relative aux cookies",
  updated: "18 septembre 2025",
  markdown: `Vaxy est une application et un nom commercial de Vaccinatieboekje B.V.

La presente politique explique comment Vaccinatieboekje B.V. ("Vaxy", "nous") utilise des cookies et technologies similaires (tels que pixels, SDK et stockage local) sur nos sites web et applications web (ensemble : le "Site"). Pour le traitement des donnees personnelles, nous renvoyons a notre declaration de confidentialite et relative aux cookies.

---

## 1. Que sont les cookies ?

Les cookies sont de petits fichiers texte places sur ton appareil lors d'une visite du Site. Par "cookies", nous entendons egalement des techniques comparables telles que pixels de suivi, balises, SDK et stockage local. Certains cookies sont necessaires au fonctionnement du Site, d'autres nous aident a mesurer les performances, ameliorer les fonctionnalites ou afficher des publicites plus pertinentes.

---

## 2. Consentement & tes choix

Lors de ta premiere visite, nous affichons une banniere de cookies avec explication. Tu peux y consentir aux cookies non essentiels (par ex. statistiques/publicite) ou definir tes preferences. Tu peux modifier ou retirer ton choix a tout moment via :

Gerer les preferences de cookies

Nous placons toujours les cookies strictement necessaires (aucun consentement requis). Pour les statistiques (anonymes/agregats), nous privilegions des parametres respectueux de la vie privee ; pour toute forme de (re)ciblage ou de suivi intersites, nous demandons un consentement prealable.

---

## 3. Quelles categories utilisons-nous ?

### 3.1. Cookies strictement necessaires (toujours actifs)

Necessaires aux fonctions de base comme la navigation, la securite (par ex. CSRF), l'equilibrage de charge et la memorisation de ton choix de cookies. Sans ces cookies, le Site ne fonctionne pas correctement. Le cookie \`vaxy_locale\` memorise ta preference linguistique et ne necessite pas de consentement.

### 3.2. Preferences / Fonctionnel (facultatif)

Memorisation des parametres (par ex. langue, region) et fonctions ameliorees comme la connexion ou l'affichage de contenus integres.

### 3.3. Statistiques / Analytics (facultatif)

Nous aide a comprendre comment le Site est utilise (pages visitees, duree des sessions, messages d'erreur) afin de l'ameliorer. Lorsque c'est possible, nous utilisons des mesures anonymisees et respectueuses de la vie privee. Nous demandons le consentement pour le suivi identifiable.

### 3.4. Marketing / Publicite (facultatif)

Utilise pour le (re)ciblage, la constitution d'audiences et la mesure des campagnes publicitaires sur des plateformes telles que Facebook/Instagram et LinkedIn. Uniquement avec ton consentement explicite.

---

## 4. Apercu d'exemples de cookies & pixels

Les cookies specifiques peuvent changer selon les mises a jour ou campagnes. Voici un apercu d'exemples de cookies et technologies de pixels couramment utilises que nous pouvons employer. Le statut actuel et le niveau de detail se trouvent dans la banniere de cookies / le gestionnaire de preferences.

| Service | Finalite / Categorie | Type / Exemple de nom | Duree de conservation | Fournisseur / Domaine |
| --- | --- | --- | --- | --- |
| First-party (Vaxy) | Necessaire (choix de cookies, session, CSRF) | cookie_consent, session_id, csrftoken | Session jusqu'a 12 mois | vaxy.app / *.vaxy.app, *.vaccinatieboekje.com |
| Analytics (Google Analytics 4) | Statistiques | _ga, _ga_*, _gid (parametres respectueux de la vie privee) | 24 heures jusqu'a 26 mois | vaxy.app / google.com |
| Meta Pixel (Facebook/Instagram) | Marketing / (Re)ciblage | _fbp + evenements pixel | 90 jours (typique) | vaxy.app / facebook.com |

Remarque : d'anciens cookies Universal Analytics (tels que __utma, __utmz) peuvent encore apparaitre dans des implementations historiques, mais nous migrons vers GA4. Consulte toujours la banniere de cookies pour la liste actuelle.

---

## 5. Google Analytics (respectueux de la vie privee)

Nous utilisons Google Analytics (de preference GA4) pour collecter des statistiques agregees. Nous avons active des parametres respectueux de la vie privee (comme le masquage IP) et conclu un accord de traitement avec Google. Les donnees ne sont pas partagees avec d'autres services Google pour les propres finalites de Google. Lorsque c'est possible, nous limitons les durees de conservation (par ex. <= 26 mois).

---

## 6. Publicite (Meta/LinkedIn) - uniquement apres consentement

Avec ton consentement, nous pouvons utiliser des cookies et pixels publicitaires de Meta (Facebook/Instagram), LinkedIn et autres afin de constituer des audiences, optimiser des campagnes et mesurer les resultats (reciblage). Tu peux retirer ton consentement a tout moment via Gerer les preferences de cookies. Sans consentement, nous ne placons pas ces cookies.

---

## 7. Transferts internationaux

Certains fournisseurs (comme Google, Meta, LinkedIn) peuvent traiter des donnees en dehors de l'Espace economique europeen. Le cas echeant, nous mettons en place des garanties appropriees (par ex. clauses contractuelles types) et limitons les flux de donnees au necessaire. Voir egalement leurs declarations de confidentialite pour plus de details.

---

## 8. Activer et desactiver les cookies (navigateur)

Tu peux egalement gerer les cookies via les parametres de ton navigateur. Note que la desactivation de tous les cookies peut limiter la fonctionnalite de certaines parties du Site. Consulte la section d'aide de ton navigateur pour les instructions.

---

## 9. Durees de conservation & suppression

La plupart des cookies expirent automatiquement apres une certaine periode ("duree d'expiration"). Tu peux aussi supprimer manuellement les cookies via ton navigateur. Pour les statistiques et journaux, nous appliquons des durees de conservation aussi courtes que possible et adaptees a la finalite (voir la declaration de confidentialite).

---

## 10. Sites web de tiers

Cette politique relative aux cookies ne s'applique pas aux sites ou services de tiers vers lesquels nous etablissons des liens. Nous te recommandons de lire les declarations de confidentialite et de cookies de ces parties.

---

## 11. Modifications

Nous pouvons modifier cette politique de temps a autre, par exemple en cas de changements de notre Site ou des technologies de cookies. Verifie donc regulierement la date "Derniere mise a jour" ci-dessus.

---

## 12. Contact

Vaccinatieboekje B.V.  
Etablie a Amsterdam  
E-mail : info@vaccinatieboekje.com`,
};

const cookiesEs: LegalDoc = {
  title: "Politica de cookies",
  updated: "18 de septiembre de 2025",
  markdown: `Vaxy es una aplicacion y nombre comercial de Vaccinatieboekje B.V.

Esta politica de cookies explica como Vaccinatieboekje B.V. ("Vaxy", "nosotros") utiliza cookies y tecnologias similares (como pixeles, SDK y almacenamiento local) en nuestro(s) sitio(s) web y aplicaciones web (conjuntamente: el "Sitio web"). Para el tratamiento de datos personales, consulta nuestra declaracion de privacidad y cookies.

---

## 1. Que son las cookies?

Las cookies son pequenos archivos de texto que se colocan en tu dispositivo al visitar el Sitio web. Por "cookies" tambien entendemos tecnicas comparables como pixeles de seguimiento, etiquetas, SDK y almacenamiento local. Algunas cookies son necesarias para el funcionamiento del Sitio web; otras nos ayudan a medir el rendimiento, mejorar la funcionalidad o mostrar anuncios mas relevantes.

---

## 2. Consentimiento y tus opciones

En tu primera visita mostramos un banner de cookies con explicacion. Alli puedes dar consentimiento para cookies no esenciales (por ejemplo estadistica/publicidad) o configurar tus preferencias. Puedes cambiar o retirar tu eleccion en cualquier momento mediante:

Gestionar preferencias de cookies

Siempre colocamos cookies estrictamente necesarias (no requieren consentimiento). Para estadisticas (anonimas/agregadas) preferimos ajustes respetuosos con la privacidad; para cualquier forma de (re)segmentacion o seguimiento entre sitios solicitamos consentimiento previo.

---

## 3. Que categorias utilizamos?

### 3.1. Cookies estrictamente necesarias (siempre activas)

Necesarias para funciones basicas como navegacion, seguridad (por ejemplo CSRF), equilibrio de carga y recordar tu eleccion de cookies. Sin estas cookies el Sitio web no funciona correctamente. La cookie \`vaxy_locale\` recuerda tu preferencia de idioma y no requiere consentimiento.

### 3.2. Preferencias / Funcional (opcional)

Recordar ajustes (por ejemplo idioma, region) y funciones mejoradas como inicio de sesion o visualizacion de contenido integrado.

### 3.3. Estadistica / Analitica (opcional)

Nos ayuda a comprender como se utiliza el Sitio web (paginas visitadas, duracion de sesion, mensajes de error) para poder mejorarlo. Cuando es posible usamos mediciones anonimizadas y respetuosas con la privacidad. Para seguimiento identificable pedimos consentimiento.

### 3.4. Marketing / Publicidad (opcional)

Se utiliza para (re)segmentacion, creacion de audiencias y medicion de campanas publicitarias en plataformas como Facebook/Instagram y LinkedIn. Solo con tu consentimiento explicito.

---

## 4. Resumen de cookies y pixeles de ejemplo

Las cookies concretas pueden cambiar por actualizacion o campana. A continuacion se muestra un resumen de ejemplo de cookies y tecnologias de pixeles de uso frecuente que podemos emplear. El estado actual y la granularidad se encuentran en el banner de cookies / gestor de preferencias.

| Servicio | Finalidad / Categoria | Tipo / Nombre de ejemplo | Periodo de conservacion | Proveedor / Dominio |
| --- | --- | --- | --- | --- |
| First-party (Vaxy) | Necesaria (eleccion de cookies, sesion, CSRF) | cookie_consent, session_id, csrftoken | Sesion hasta 12 meses | vaxy.app / *.vaxy.app, *.vaccinatieboekje.com |
| Analytics (Google Analytics 4) | Estadistica | _ga, _ga_*, _gid (configuracion respetuosa con la privacidad) | 24 horas hasta 26 meses | vaxy.app / google.com |
| Meta Pixel (Facebook/Instagram) | Marketing / (Re)segmentacion | _fbp + eventos de pixel | 90 dias (tipico) | vaxy.app / facebook.com |

Nota: las cookies antiguas de Universal Analytics (como __utma, __utmz) pueden seguir apareciendo en implementaciones historicas, pero estamos migrando a GA4. Consulta siempre el banner de cookies para la lista actual.

---

## 5. Google Analytics (respetuoso con la privacidad)

Usamos Google Analytics (preferiblemente GA4) para recopilar estadisticas agregadas. Hemos activado ajustes respetuosos con la privacidad (como enmascaramiento de IP) y celebrado un acuerdo de tratamiento con Google. Los datos no se comparten con otros servicios de Google para fines propios de Google. Cuando es posible limitamos los periodos de conservacion (por ejemplo <= 26 meses).

---

## 6. Publicidad (Meta/LinkedIn) - solo tras consentimiento

Con tu consentimiento podemos utilizar cookies y pixeles publicitarios de Meta (Facebook/Instagram), LinkedIn y otros para crear audiencias, optimizar campanas y medir resultados (retargeting). Puedes retirar tu consentimiento en cualquier momento mediante Gestionar preferencias de cookies. Sin consentimiento no colocamos estas cookies.

---

## 7. Transferencias internacionales

Algunos proveedores (como Google, Meta y LinkedIn) pueden tratar datos fuera del Espacio Economico Europeo. Cuando corresponda, adoptamos garantias adecuadas (por ejemplo clausulas contractuales tipo) y limitamos los flujos de datos a lo necesario. Consulta tambien sus declaraciones de privacidad para mas detalles.

---

## 8. Activar y desactivar cookies (navegador)

Tambien puedes gestionar cookies desde la configuracion de tu navegador. Ten en cuenta que desactivar todas las cookies puede limitar la funcionalidad de partes del Sitio web. Consulta la seccion de ayuda de tu navegador para instrucciones.

---

## 9. Periodos de conservacion y eliminacion

La mayoria de las cookies caducan automaticamente tras un periodo determinado ("tiempo de expiracion"). Tambien puedes eliminar cookies manualmente desde tu navegador. Para estadisticas y registros aplicamos periodos de conservacion lo mas cortos posible y adecuados a la finalidad (vease la declaracion de privacidad).

---

## 10. Sitios web de terceros

Esta politica de cookies no se aplica a sitios web o servicios de terceros a los que enlazamos. Recomendamos leer las declaraciones de privacidad y cookies de esas partes.

---

## 11. Cambios

Podemos modificar esta politica de cookies de vez en cuando, por ejemplo por cambios en nuestro Sitio web o en la tecnologia de cookies. Por ello, comprueba periodicamente la fecha de "Ultima actualizacion" indicada arriba.

---

## 12. Contacto

Vaccinatieboekje B.V.  
Establecida en Amsterdam  
Correo electronico: info@vaccinatieboekje.com`,
};

const termsNl: LegalDoc = {
  title: "Gebruiksvoorwaarden",
  updated: "18-09-2025",
  markdown: `Vaxy is een app en handelsnaam van Vaccinatieboekje B.V.

Versie: 2.0  
Datum: 18-09-2025

Deze Gebruiksvoorwaarden zijn van toepassing op het gebruik van de mobiele applicatie Vaxy en de bijbehorende webapplicatie en websites (gezamenlijk: de App), die worden aangeboden door Vaccinatieboekje B.V., gevestigd aan Bilderdijkkade 48, 1053 GJ Amsterdam, ingeschreven bij de KvK onder nummer 69402574 (Vaccinatieboekje, wij, ons).

---

## 1. Definities

In deze voorwaarden hebben met hoofdletter geschreven begrippen (zowel enkelvoud als meervoud) de volgende betekenis:

- Account: het persoonlijke gebruiksprofiel dat Gebruiker aanmaakt om de App te gebruiken (incl. sub-accounts/familieprofielen).
- App: de mobiele app en webapplicatie "Vaxy", eigendom van Vaccinatieboekje B.V.
- Gebruiker: de natuurlijke persoon die niet handelt in de uitoefening van beroep of bedrijf en de App gebruikt of een Account aanmaakt.
- Overeenkomst: de overeenkomst tussen Gebruiker en Vaccinatieboekje inzake het gebruik van de App, waarvan deze voorwaarden onderdeel uitmaken.
- Intellectuele Eigendomsrechten: alle IE-rechten, waaronder auteurs-, databank-, handelsnaam-, merk-, model- en octrooirechten, en knowhow.

---

## 2. Totstandkoming & Account

- De App is te downloaden via de Apple App Store en Google Play Store. Registratie is vereist voor gebruik van (delen van) de App.
- De Overeenkomst komt tot stand bij installatie van de App en/of registratie van het Account.
- Indien (in-app) betalingen gelden, wordt dit vooraf duidelijk vermeld. Aankopen verlopen via de betreffende appstore-betaalsystemen.
- Gebruiker kiest een sterk wachtwoord en is verantwoordelijk voor geheimhouding van inloggegevens. Wij mogen erop vertrouwen dat acties via het Account door Gebruiker zijn verricht.
- Gebruiker kan sub-accounts/familieprofielen beheren. Bij loskoppeling kan een zelfstandig Account ontstaan.

---

## 3. Functionaliteit van de App

- Met de App kan Gebruiker (a) informatie over vaccinaties en infectieziekten raadplegen en (b) eigen vaccinaties registreren (bijv. dosis, datum, merk, batch, geldigheid) en aanwijzingen/foto's opslaan (bijv. van een papieren boekje).
- Geen medisch advies: de App is een digitaal (vaccineer)notitieboekje en informatieservice. De App geeft geen medisch advies, diagnose of behandeling. Voor medische vragen, reisvaccinaties en beslissingen dient Gebruiker altijd een arts of bevoegde instelling te raadplegen. Gebruiker is verantwoordelijk voor juistheid en volledigheid van eigen invoer.
- Gebruiker kan overzichten exporteren (bijv. PDF) of per e-mail ontvangen.
- Wij spannen ons in om informatie actueel en juist te houden, maar garanderen dit niet. Gebruik is voor eigen rekening en risico (zie ook artikel 12).

---

## 4. Afspraken & Externe Zorgaanbieders

- De App kan (nu of in de toekomst) het maken van afspraken bij externe zorgaanbieders (zoals Vaccinatiepunt) faciliteren. Voor het inplannen kan het nodig zijn dat wij, uitsluitend met jouw expliciete toestemming, gegevens (zoals naam, contactgegevens, gewenste vaccinatie) delen met de betreffende aanbieder.
- Na overdracht aan de zorgaanbieder verwerkt die partij de gegevens als zelfstandig verwerkingsverantwoordelijke onder eigen privacyvoorwaarden. Lees die voorwaarden altijd zorgvuldig.
- Geef je geen toestemming, dan vindt geen gegevensdeling plaats en kan geen afspraak via de App worden geboekt.

---

## 5. Gebruiksregels

- Gebruik de App conform deze voorwaarden en toepasselijke wetgeving. Verboden zijn o.a.: onrechtmatig, misleidend of beledigend gebruik; inbreuk op IE-rechten; het verstoren van de App (bijv. malware, scraping); of het omzeilen van beveiliging.
- Bij (vermoeden van) overtreding mogen wij passende maatregelen nemen, waaronder (tijdelijke) blokkade van Account/toegang en verwijdering van onrechtmatige inhoud.
- Wij kunnen meewerken aan rechtmatige verzoeken van bevoegde autoriteiten en - onder de voorwaarden van de wet - identificerende gegevens verstrekken bij duidelijke onrechtmatigheid.

---

## 6. Betaalde diensten & Abonnementen

- Sommige functies zijn betaald (bijv. Premium). Prijzen en voorwaarden worden vooraf getoond. In-app aankopen verlopen via Apple/Google; bevestiging door het storesysteem is vereist.
- Abonnementen kunnen automatisch verlengen volgens de voorwaarden van de appstore. Beheer en opzegging verlopen via je store-accountinstellingen. Eventuele proefperiodes en promoties worden vooraf vermeld.
- Voor zover wettelijk toegestaan, geldt geen bedenktijd/ontbinding voor reeds geleverde digitale inhoud via de App Store/Google Play (store-regels zijn leidend).

---

## 7. Intellectuele Eigendom & Licentie

- Alle IE-rechten op de App, content en onderliggende technologie behoren toe aan Vaccinatieboekje of haar licentiegevers. Gebruiker verkrijgt een persoonlijk, herroepbaar, niet-exclusief, niet-overdraagbaar gebruiksrecht voor de duur van de Overeenkomst.
- Het is niet toegestaan de App of onderdelen daarvan te kopieren, verkopen, verhuren, sublicentieren, reverse engineeren, (de)compileren of beveiligingen te omzeilen, tenzij dwingend recht dit toestaat.
- Merken, logo's en handelsnamen (waaronder "Vaxy") zijn beschermd. Vermeldingen van IE-rechten of vertrouwelijkheid mogen niet worden verwijderd.
- Door het geven van feedback/suggesties verleen je ons een kosteloze, wereldwijde, overdraagbare licentie om dergelijke input te gebruiken voor het verbeteren en ontwikkelen van de App.

---

## 8. Beveiliging & Privacy

- Wij treffen passende technische en organisatorische maatregelen om de App te beveiligen.
- Persoonsgegevens worden verwerkt conform onze Privacy- en cookieverklaring. Deze maakt geen onderdeel uit van deze voorwaarden maar is wel van toepassing op het gebruik van de App.
- Wij mogen je per e-mail serviceberichten sturen die nodig zijn voor de App, en incidenteel uitnodigen tot een vrijwillige enquête over de App. Enquêtes kun je altijd weigeren of afmelden via de afmeldlink. Promotionele berichten sturen we alleen met jouw toestemming. Zie de Privacyverklaring.

---

## 9. Data van Gebruiker

- Gegevens die jij invoert blijven in eigendom van jou. Voor het kunnen leveren van de App verleen je ons een beperkte licentie om deze gegevens te hosten, te verwerken en weer te geven, zolang je Account actief is en conform de Privacyverklaring.
- Je kunt gegevens verwijderen via de App. Bij beëindiging kun je - voor zover technisch redelijk - een kopie/export opvragen in een gangbaar formaat (zie ook onze Privacyverklaring voor modaliteiten en termijnen).
- Jij bent verantwoordelijk voor de juistheid, kwaliteit en rechtmatigheid van de door jou ingevoerde gegevens en vrijwaart ons voor aanspraken van derden hieruit voortvloeiend.

---

## 10. Beschikbaarheid, Onderhoud & Wijzigingen

- Wij streven naar hoge beschikbaarheid maar geven geen garantie op foutloze of ononderbroken werking. Onderhoud, updates of storingen kunnen (tijdelijke) onderbrekingen veroorzaken.
- Wij mogen (delen van) de App wijzigen, toevoegen of verwijderen (waaronder beta- of testfunctionaliteit). Als een wijziging de kernfunctionaliteit voor consumenten wezenlijk beperkt, informeren wij je daarover op redelijke wijze.
- Updates verlopen via de App Store/Google Play. Je bent zelf verantwoordelijk voor installatie van updates; het niet installeren kan de werking en veiligheid nadelig beïnvloeden.

---

## 11. Informatie van Derden

- De App kan links of integraties met websites/diensten van derden bevatten (bijv. zorgaanbieders, appstores). Wij zijn niet verantwoordelijk voor (de inhoud van) die derden of voor de beschikbaarheid daarvan.

---

## 12. Aansprakelijkheid

- Geen medisch advies: de App is informatief en administratief. Medische beslissingen neem je nooit uitsluitend op basis van de App.
- Voor zover wettelijk toegestaan, sluiten wij aansprakelijkheid uit voor indirecte schade, gevolgschade, gederfde winst, verlies van gegevens, reputatieschade of schade door onjuist gebruik van de App.
- Onze totale aansprakelijkheid voor directe schade is, voor zover wettelijk toegestaan, beperkt tot (a) het bedrag dat onze aansprakelijkheidsverzekeraar uitkeert of, als dat lager is, (b) EUR 100 per gebeurtenis (max. EUR 300 per jaar).
- De aansprakelijkheidsbeperkingen gelden niet bij opzet of bewuste roekeloosheid van onze tot de bedrijfsleiding behorende leidinggevenden.
- Wij zijn niet aansprakelijk voor gebreken in of wijzigingen van App Store/Google Play of betaalplatforms.

---

## 13. Klachten & Support

- We behandelen vragen/klachten serieus. Neem contact op via de App of onderstaande contactgegevens. We reageren zo spoedig mogelijk en in elk geval binnen 14 dagen.

---

## 14. Duur & Beëindiging

- De Overeenkomst loopt zolang je de App gebruikt en/of een Account hebt. Jij kunt altijd opzeggen door je Account te verwijderen en de App te de-installeren.
- Wij kunnen de Overeenkomst (gedeeltelijk) beëindigen of opschorten als je deze voorwaarden of toepasselijke wet overtreedt, of als (naar objectieve maatstaven) misbruik of veiligheidsrisico's bestaan.
- Na beëindiging stop je onmiddellijk met gebruik. Exporteer vooraf je gegevens waar mogelijk.

---

## 15. Wijziging van deze voorwaarden

- Wij mogen deze voorwaarden wijzigen. We communiceren substantiële wijzigingen ten minste 30 dagen vooraf (bijv. via e-mail of in-app melding). Gebruik van de App na de ingangsdatum betekent acceptatie van de nieuwe voorwaarden.
- Ga je niet akkoord met een materiële wijziging, dan kun je de Overeenkomst kosteloos beëindigen vóór de ingangsdatum.

---

## 16. Toepasselijk recht & Forumkeuze

- Op deze voorwaarden en de Overeenkomst is Nederlands recht van toepassing. Deze rechtskeuze laat dwingendrechtelijke consumentenbescherming van jouw woonstaat onverlet.
- Voor zover dwingend recht niet anders bepaalt, zijn geschillen onderworpen aan de bevoegde rechter te Amsterdam.

---

## 17. Overige bepalingen

- Als een bepaling nietig/vernietigbaar is, blijft de rest van kracht; wij vervangen de ongeldige bepaling door een geldige bepaling die de bedoeling het dichtst benadert.
- "Schriftelijk" omvat ook e-mail, mits de herkomst en integriteit voldoende vaststaan.
- Wij mogen rechten en verplichtingen uit de Overeenkomst overdragen in het kader van bijvoorbeeld een herstructurering of overdracht van (delen van) onze onderneming, met inachtneming van toepasselijk recht.

---

## 18. Contact

Vaccinatieboekje B.V.  
Bilderdijkkade 48  
1053 GJ Amsterdam  
KvK 69402574  
E-mail: info@vaccinatieboekje.com`,
};

const termsEn: LegalDoc = {
  title: "Terms of Use",
  updated: "18-09-2025",
  markdown: `Vaxy is an app and trade name of Vaccinatieboekje B.V.

Version: 2.0  
Date: 18-09-2025

These Terms of Use apply to the use of the Vaxy mobile application and the associated web application and websites (together: the App), offered by Vaccinatieboekje B.V., located at Bilderdijkkade 48, 1053 GJ Amsterdam, registered with the Chamber of Commerce under number 69402574 (Vaccinatieboekje, we, us).

---

## 1. Definitions

In these terms, capitalized terms (singular and plural) have the following meanings:

- Account: the personal user profile that the User creates to use the App (including sub-accounts/family profiles).
- App: the mobile app and web application "Vaxy", owned by Vaccinatieboekje B.V.
- User: the natural person who is not acting in the exercise of a profession or business and who uses the App or creates an Account.
- Agreement: the agreement between the User and Vaccinatieboekje regarding use of the App, of which these terms form part.
- Intellectual Property Rights: all IP rights, including copyrights, database rights, trade name rights, trademarks, design rights, patent rights and know-how.

---

## 2. Formation & Account

- The App can be downloaded via the Apple App Store and Google Play Store. Registration is required to use parts of the App.
- The Agreement is formed upon installation of the App and/or registration of the Account.
- If in-app payments apply, this will be clearly stated in advance. Purchases are made through the relevant app store payment systems.
- The User chooses a strong password and is responsible for keeping login details confidential. We may rely on actions through the Account being performed by the User.
- The User may manage sub-accounts/family profiles. Upon unlinking, an independent Account may be created.

---

## 3. Functionality of the App

- With the App, the User can (a) consult information about vaccinations and infectious diseases and (b) record their own vaccinations (for example dose, date, brand, batch, validity) and store notes/photos (for example of a paper booklet).
- No medical advice: the App is a digital vaccination notebook and information service. The App does not provide medical advice, diagnosis or treatment. For medical questions, travel vaccinations and decisions, the User must always consult a doctor or authorized institution. The User is responsible for the accuracy and completeness of their own input.
- The User can export overviews (for example PDF) or receive them by email.
- We endeavor to keep information current and accurate, but do not guarantee this. Use is at the User's own expense and risk (see also Article 12).

---

## 4. Appointments & External Healthcare Providers

- The App may now or in the future facilitate making appointments with external healthcare providers (such as Vaccinatiepunt). For scheduling, it may be necessary for us, only with your explicit consent, to share data (such as name, contact details, desired vaccination) with the relevant provider.
- After transfer to the healthcare provider, that party processes the data as an independent controller under its own privacy terms. Always read those terms carefully.
- If you do not give consent, no data sharing will take place and no appointment can be booked via the App.

---

## 5. Rules of Use

- Use the App in accordance with these terms and applicable law. Prohibited uses include unlawful, misleading or offensive use; infringement of IP rights; disruption of the App (for example malware, scraping); or circumvention of security.
- In the event of a suspected or actual breach, we may take appropriate measures, including temporary blocking of the Account/access and removal of unlawful content.
- We may cooperate with lawful requests from competent authorities and, subject to legal conditions, provide identifying data in cases of clear unlawfulness.

---

## 6. Paid Services & Subscriptions

- Some functions are paid (for example Premium). Prices and terms are shown in advance. In-app purchases are made through Apple/Google; confirmation by the store system is required.
- Subscriptions may renew automatically according to the app store terms. Management and cancellation take place via your store account settings. Any trial periods and promotions are stated in advance.
- To the extent permitted by law, there is no cooling-off period/right of withdrawal for digital content already supplied via the App Store/Google Play (store rules prevail).

---

## 7. Intellectual Property & License

- All IP rights to the App, content and underlying technology belong to Vaccinatieboekje or its licensors. The User receives a personal, revocable, non-exclusive, non-transferable right of use for the duration of the Agreement.
- It is not permitted to copy, sell, rent, sublicense, reverse engineer, decompile or circumvent security of the App or parts thereof, unless mandatory law permits this.
- Brands, logos and trade names (including "Vaxy") are protected. Notices of IP rights or confidentiality may not be removed.
- By providing feedback/suggestions, you grant us a free, worldwide, transferable license to use such input to improve and develop the App.

---

## 8. Security & Privacy

- We take appropriate technical and organizational measures to secure the App.
- Personal data are processed in accordance with our Privacy and cookie statement. It does not form part of these terms but does apply to use of the App.
- We may send you service emails required for the App, and occasionally invite you to a voluntary survey about the App. You can always decline or unsubscribe via the unsubscribe link. Promotional messages are sent only with your consent. See the Privacy Statement.

---

## 9. User Data

- Data you enter remain your property. To provide the App, you grant us a limited license to host, process and display these data while your Account is active and in accordance with the Privacy Statement.
- You can delete data via the App. Upon termination, you may request a copy/export in a common format insofar as technically reasonable (see also our Privacy Statement for modalities and periods).
- You are responsible for the accuracy, quality and lawfulness of the data you enter and indemnify us against third-party claims arising from them.

---

## 10. Availability, Maintenance & Changes

- We strive for high availability but do not guarantee error-free or uninterrupted operation. Maintenance, updates or outages may cause temporary interruptions.
- We may change, add or remove parts of the App, including beta or test functionality. If a change materially limits the core functionality for consumers, we will inform you in a reasonable manner.
- Updates are provided via the App Store/Google Play. You are responsible for installing updates; failure to do so may adversely affect operation and security.

---

## 11. Third-Party Information

- The App may contain links or integrations with third-party websites/services (for example healthcare providers, app stores). We are not responsible for the content of those third parties or their availability.

---

## 12. Liability

- No medical advice: the App is informational and administrative. You should never make medical decisions solely on the basis of the App.
- To the extent permitted by law, we exclude liability for indirect damage, consequential damage, lost profits, loss of data, reputational damage or damage caused by incorrect use of the App.
- Our total liability for direct damage is, to the extent permitted by law, limited to (a) the amount paid by our liability insurer or, if lower, (b) EUR 100 per event (maximum EUR 300 per year).
- The liability limitations do not apply in cases of intent or deliberate recklessness by our managing executives.
- We are not liable for defects in or changes to the App Store/Google Play or payment platforms.

---

## 13. Complaints & Support

- We take questions/complaints seriously. Contact us via the App or the contact details below. We respond as soon as possible and in any event within 14 days.

---

## 14. Term & Termination

- The Agreement runs as long as you use the App and/or have an Account. You can terminate at any time by deleting your Account and uninstalling the App.
- We may partially or fully terminate or suspend the Agreement if you breach these terms or applicable law, or if abuse or security risks exist by objective standards.
- After termination, you must immediately stop using the App. Export your data beforehand where possible.

---

## 15. Changes to these terms

- We may amend these terms. We communicate substantial changes at least 30 days in advance (for example by email or in-app notice). Use of the App after the effective date means acceptance of the new terms.
- If you do not agree to a material change, you may terminate the Agreement free of charge before the effective date.

---

## 16. Applicable law & Forum

- These terms and the Agreement are governed by Dutch law. This choice of law does not affect mandatory consumer protection in your state of residence.
- Unless mandatory law provides otherwise, disputes are subject to the competent court in Amsterdam.

---

## 17. Miscellaneous

- If a provision is null or voidable, the rest remains in force; we will replace the invalid provision with a valid provision that most closely approximates the intention.
- "In writing" also includes email, provided origin and integrity are sufficiently established.
- We may transfer rights and obligations under the Agreement in the context of, for example, a restructuring or transfer of all or part of our business, with due observance of applicable law.

---

## 18. Contact

Vaccinatieboekje B.V.  
Bilderdijkkade 48  
1053 GJ Amsterdam  
Chamber of Commerce 69402574  
Email: info@vaccinatieboekje.com`,
};

const termsDe: LegalDoc = {
  title: "Nutzungsbedingungen",
  updated: "18.09.2025",
  markdown: `Vaxy ist eine App und ein Handelsname von Vaccinatieboekje B.V.

Version: 2.0  
Datum: 18.09.2025

Diese Nutzungsbedingungen gelten fuer die Nutzung der mobilen Anwendung Vaxy und der zugehoerigen Webanwendung und Websites (gemeinsam: die App), angeboten von Vaccinatieboekje B.V., ansaessig Bilderdijkkade 48, 1053 GJ Amsterdam, eingetragen bei der KvK unter Nummer 69402574 (Vaccinatieboekje, wir, uns).

---

## 1. Definitionen

In diesen Bedingungen haben grossgeschriebene Begriffe (Singular und Plural) folgende Bedeutung:

- Account: das persoenliche Nutzungsprofil, das der Nutzer erstellt, um die App zu verwenden (einschliesslich Unterkonten/Familienprofilen).
- App: die mobile App und Webanwendung "Vaxy", Eigentum von Vaccinatieboekje B.V.
- Nutzer: die natuerliche Person, die nicht in Ausuebung eines Berufs oder Gewerbes handelt und die App nutzt oder einen Account erstellt.
- Vereinbarung: die Vereinbarung zwischen Nutzer und Vaccinatieboekje ueber die Nutzung der App, deren Bestandteil diese Bedingungen sind.
- Geistige Eigentumsrechte: alle IP-Rechte, darunter Urheber-, Datenbank-, Handelsnamen-, Marken-, Design- und Patentrechte sowie Know-how.

---

## 2. Zustandekommen & Account

- Die App kann ueber den Apple App Store und Google Play Store heruntergeladen werden. Fuer die Nutzung von Teilen der App ist eine Registrierung erforderlich.
- Die Vereinbarung kommt durch Installation der App und/oder Registrierung des Accounts zustande.
- Wenn In-App-Zahlungen gelten, wird dies vorab klar angegeben. Kaeufe laufen ueber die jeweiligen App-Store-Zahlungssysteme.
- Der Nutzer waehlt ein starkes Passwort und ist fuer die Geheimhaltung der Zugangsdaten verantwortlich. Wir duerfen darauf vertrauen, dass Handlungen ueber den Account vom Nutzer vorgenommen wurden.
- Der Nutzer kann Unterkonten/Familienprofile verwalten. Bei Trennung kann ein eigenstaendiger Account entstehen.

---

## 3. Funktionalitaet der App

- Mit der App kann der Nutzer (a) Informationen ueber Impfungen und Infektionskrankheiten abrufen und (b) eigene Impfungen registrieren (z. B. Dosis, Datum, Marke, Charge, Gueltigkeit) sowie Hinweise/Fotos speichern (z. B. aus einem Papierheft).
- Keine medizinische Beratung: Die App ist ein digitales Impfnotizbuch und Informationsdienst. Die App bietet keine medizinische Beratung, Diagnose oder Behandlung. Bei medizinischen Fragen, Reiseimpfungen und Entscheidungen muss der Nutzer stets einen Arzt oder eine befugte Einrichtung konsultieren. Der Nutzer ist fuer Richtigkeit und Vollstaendigkeit eigener Eingaben verantwortlich.
- Der Nutzer kann Uebersichten exportieren (z. B. PDF) oder per E-Mail erhalten.
- Wir bemuehen uns, Informationen aktuell und richtig zu halten, garantieren dies jedoch nicht. Die Nutzung erfolgt auf eigene Kosten und eigenes Risiko (siehe auch Artikel 12).

---

## 4. Termine & externe Gesundheitsdienstleister

- Die App kann jetzt oder kuenftig Termine bei externen Gesundheitsdienstleistern (wie Vaccinatiepunt) erleichtern. Fuer die Planung kann es erforderlich sein, dass wir, ausschliesslich mit deiner ausdruecklichen Einwilligung, Daten (wie Name, Kontaktdaten, gewuenschte Impfung) an den betreffenden Anbieter weitergeben.
- Nach der Uebermittlung an den Gesundheitsdienstleister verarbeitet diese Partei die Daten als eigenstaendiger Verantwortlicher nach eigenen Datenschutzbedingungen. Lies diese Bedingungen immer sorgfaeltig.
- Erteilst du keine Einwilligung, findet keine Datenweitergabe statt und es kann kein Termin ueber die App gebucht werden.

---

## 5. Nutzungsregeln

- Nutze die App gemaess diesen Bedingungen und anwendbarem Recht. Verboten sind u. a. rechtswidrige, irrefuehrende oder beleidigende Nutzung; Verletzung von IP-Rechten; Stoerung der App (z. B. Malware, Scraping); oder Umgehung von Sicherheit.
- Bei vermuteter oder tatsaechlicher Verletzung duerfen wir geeignete Massnahmen ergreifen, darunter temporaere Sperrung von Account/Zugang und Entfernung rechtswidriger Inhalte.
- Wir koennen rechtmaessigen Ersuchen zustaendiger Behoerden nachkommen und, unter den gesetzlichen Voraussetzungen, identifizierende Daten bei eindeutiger Rechtswidrigkeit bereitstellen.

---

## 6. Kostenpflichtige Dienste & Abonnements

- Einige Funktionen sind kostenpflichtig (z. B. Premium). Preise und Bedingungen werden vorab angezeigt. In-App-Kaeufe laufen ueber Apple/Google; eine Bestaetigung durch das Storesystem ist erforderlich.
- Abonnements koennen sich nach den Bedingungen des App Stores automatisch verlaengern. Verwaltung und Kuendigung erfolgen ueber deine Store-Kontoeinstellungen. Etwaige Testzeiträume und Aktionen werden vorab angegeben.
- Soweit gesetzlich zulaessig, gilt keine Widerrufsfrist/kein Ruecktritt fuer bereits gelieferte digitale Inhalte ueber den App Store/Google Play (Store-Regeln sind massgeblich).

---

## 7. Geistiges Eigentum & Lizenz

- Alle IP-Rechte an der App, den Inhalten und der zugrunde liegenden Technologie gehoeren Vaccinatieboekje oder ihren Lizenzgebern. Der Nutzer erhaelt fuer die Dauer der Vereinbarung ein persoenliches, widerrufliches, nicht exklusives, nicht uebertragbares Nutzungsrecht.
- Es ist nicht gestattet, die App oder Teile davon zu kopieren, zu verkaufen, zu vermieten, unterzulizenzieren, zurueckzuentwickeln, zu dekompilieren oder Sicherheitsmechanismen zu umgehen, sofern zwingendes Recht dies nicht erlaubt.
- Marken, Logos und Handelsnamen (einschliesslich "Vaxy") sind geschuetzt. Hinweise auf IP-Rechte oder Vertraulichkeit duerfen nicht entfernt werden.
- Durch Feedback/Vorschlaege gewaehrst du uns eine kostenlose, weltweite, uebertragbare Lizenz, diese Eingaben zur Verbesserung und Entwicklung der App zu verwenden.

---

## 8. Sicherheit & Datenschutz

- Wir treffen geeignete technische und organisatorische Massnahmen, um die App zu sichern.
- Personenbezogene Daten werden gemaess unserer Datenschutz- und Cookie-Erklaerung verarbeitet. Diese ist kein Bestandteil dieser Bedingungen, gilt aber fuer die Nutzung der App.
- Wir duerfen dir Service-E-Mails senden, die fuer die App erforderlich sind, und dich gelegentlich zu einer freiwilligen Umfrage ueber die App einladen. Umfragen kannst du jederzeit ablehnen oder ueber den Abmeldelink abbestellen. Werbliche Nachrichten senden wir nur mit deiner Einwilligung. Siehe die Datenschutzerklaerung.

---

## 9. Nutzerdaten

- Daten, die du eingibst, bleiben dein Eigentum. Zur Bereitstellung der App gewaehrst du uns eine beschraenkte Lizenz, diese Daten zu hosten, zu verarbeiten und anzuzeigen, solange dein Account aktiv ist und gemaess der Datenschutzerklaerung.
- Du kannst Daten ueber die App loeschen. Bei Beendigung kannst du, soweit technisch angemessen, eine Kopie/einen Export in einem gaengigen Format anfordern (siehe auch unsere Datenschutzerklaerung zu Modalitaeten und Fristen).
- Du bist fuer Richtigkeit, Qualitaet und Rechtmaessigkeit der von dir eingegebenen Daten verantwortlich und stellst uns von daraus resultierenden Anspruechen Dritter frei.

---

## 10. Verfuegbarkeit, Wartung & Aenderungen

- Wir streben hohe Verfuegbarkeit an, garantieren aber keinen fehlerfreien oder ununterbrochenen Betrieb. Wartung, Updates oder Stoerungen koennen temporaere Unterbrechungen verursachen.
- Wir duerfen Teile der App aendern, hinzufuegen oder entfernen, einschliesslich Beta- oder Testfunktionen. Wenn eine Aenderung die Kernfunktion fuer Verbraucher wesentlich einschraenkt, informieren wir dich in angemessener Weise.
- Updates erfolgen ueber den App Store/Google Play. Du bist fuer die Installation von Updates verantwortlich; Nichtinstallation kann Funktion und Sicherheit beeintraechtigen.

---

## 11. Informationen Dritter

- Die App kann Links oder Integrationen mit Websites/Diensten Dritter enthalten (z. B. Gesundheitsdienstleister, App Stores). Wir sind nicht verantwortlich fuer deren Inhalt oder Verfuegbarkeit.

---

## 12. Haftung

- Keine medizinische Beratung: Die App ist informativ und administrativ. Medizinische Entscheidungen triffst du niemals ausschliesslich auf Grundlage der App.
- Soweit gesetzlich zulaessig, schliessen wir Haftung fuer indirekte Schaeden, Folgeschaeden, entgangenen Gewinn, Datenverlust, Reputationsschaeden oder Schaeden durch falsche Nutzung der App aus.
- Unsere Gesamthaftung fuer direkte Schaeden ist, soweit gesetzlich zulaessig, beschraenkt auf (a) den Betrag, den unsere Haftpflichtversicherung auszahlt, oder, falls niedriger, (b) EUR 100 pro Ereignis (max. EUR 300 pro Jahr).
- Die Haftungsbeschraenkungen gelten nicht bei Vorsatz oder bewusster Leichtfertigkeit unserer zur Geschaeftsleitung gehoerenden Fuehrungskraefte.
- Wir haften nicht fuer Maengel oder Aenderungen des App Store/Google Play oder von Zahlungsplattformen.

---

## 13. Beschwerden & Support

- Wir nehmen Fragen/Beschwerden ernst. Kontaktiere uns ueber die App oder die unten stehenden Kontaktdaten. Wir antworten so bald wie moeglich und jedenfalls innerhalb von 14 Tagen.

---

## 14. Laufzeit & Beendigung

- Die Vereinbarung laeuft, solange du die App nutzt und/oder einen Account hast. Du kannst jederzeit kuendigen, indem du dein Account loeschst und die App deinstallierst.
- Wir koennen die Vereinbarung ganz oder teilweise beenden oder aussetzen, wenn du diese Bedingungen oder anwendbares Recht verletzt oder wenn nach objektiven Massstaeben Missbrauch oder Sicherheitsrisiken bestehen.
- Nach Beendigung stellst du die Nutzung sofort ein. Exportiere deine Daten vorher, soweit moeglich.

---

## 15. Aenderung dieser Bedingungen

- Wir duerfen diese Bedingungen aendern. Wesentliche Aenderungen teilen wir mindestens 30 Tage vorher mit (z. B. per E-Mail oder In-App-Mitteilung). Nutzung der App nach Inkrafttreten bedeutet Annahme der neuen Bedingungen.
- Stimmst du einer wesentlichen Aenderung nicht zu, kannst du die Vereinbarung vor Inkrafttreten kostenlos beenden.

---

## 16. Anwendbares Recht & Gerichtsstand

- Auf diese Bedingungen und die Vereinbarung findet niederlaendisches Recht Anwendung. Diese Rechtswahl laesst zwingenden Verbraucherschutz deines Wohnsitzstaats unberuehrt.
- Soweit zwingendes Recht nichts anderes bestimmt, unterliegen Streitigkeiten dem zustaendigen Gericht in Amsterdam.

---

## 17. Sonstige Bestimmungen

- Ist eine Bestimmung nichtig/anfechtbar, bleibt der Rest wirksam; wir ersetzen die unwirksame Bestimmung durch eine wirksame, die der Absicht am naechsten kommt.
- "Schriftlich" umfasst auch E-Mail, sofern Herkunft und Integritaet ausreichend feststehen.
- Wir duerfen Rechte und Pflichten aus der Vereinbarung im Rahmen z. B. einer Umstrukturierung oder Uebertragung von Teilen unseres Unternehmens unter Beachtung anwendbaren Rechts uebertragen.

---

## 18. Kontakt

Vaccinatieboekje B.V.  
Bilderdijkkade 48  
1053 GJ Amsterdam  
KvK 69402574  
E-Mail: info@vaccinatieboekje.com`,
};

const termsFr: LegalDoc = {
  title: "Conditions d'utilisation",
  updated: "18-09-2025",
  markdown: `Vaxy est une application et un nom commercial de Vaccinatieboekje B.V.

Version : 2.0  
Date : 18-09-2025

Les presentes Conditions d'utilisation s'appliquent a l'utilisation de l'application mobile Vaxy ainsi que de l'application web et des sites associes (ensemble : l'App), proposes par Vaccinatieboekje B.V., situee Bilderdijkkade 48, 1053 GJ Amsterdam, inscrite a la KvK sous le numero 69402574 (Vaccinatieboekje, nous).

---

## 1. Definitions

Dans ces conditions, les termes commencant par une majuscule (singulier et pluriel) ont la signification suivante :

- Compte : le profil utilisateur personnel que l'Utilisateur cree pour utiliser l'App (y compris sous-comptes/profils familiaux).
- App : l'application mobile et l'application web "Vaxy", propriete de Vaccinatieboekje B.V.
- Utilisateur : la personne physique qui n'agit pas dans l'exercice d'une profession ou d'une entreprise et qui utilise l'App ou cree un Compte.
- Contrat : le contrat entre l'Utilisateur et Vaccinatieboekje concernant l'utilisation de l'App, dont ces conditions font partie.
- Droits de propriete intellectuelle : tous les droits de PI, y compris droits d'auteur, droits de base de donnees, noms commerciaux, marques, dessins et modeles, brevets et savoir-faire.

---

## 2. Formation & Compte

- L'App peut etre telechargee via l'Apple App Store et le Google Play Store. L'inscription est requise pour utiliser certaines parties de l'App.
- Le Contrat est forme lors de l'installation de l'App et/ou de l'inscription du Compte.
- Si des paiements integres s'appliquent, cela est clairement indique a l'avance. Les achats passent par les systemes de paiement de la boutique d'applications concernee.
- L'Utilisateur choisit un mot de passe fort et est responsable de la confidentialite des identifiants. Nous pouvons supposer que les actions effectuees via le Compte l'ont ete par l'Utilisateur.
- L'Utilisateur peut gerer des sous-comptes/profils familiaux. En cas de dissociation, un Compte independant peut etre cree.

---

## 3. Fonctionnalite de l'App

- Avec l'App, l'Utilisateur peut (a) consulter des informations sur les vaccinations et maladies infectieuses et (b) enregistrer ses propres vaccinations (par ex. dose, date, marque, lot, validite) et conserver des notes/photos (par ex. d'un carnet papier).
- Pas d'avis medical : l'App est un carnet de vaccination numerique et un service d'information. L'App ne fournit pas d'avis medical, de diagnostic ou de traitement. Pour les questions medicales, les vaccinations de voyage et les decisions, l'Utilisateur doit toujours consulter un medecin ou un organisme habilite. L'Utilisateur est responsable de l'exactitude et de l'exhaustivite de ses propres saisies.
- L'Utilisateur peut exporter des apercus (par ex. PDF) ou les recevoir par e-mail.
- Nous nous efforcons de maintenir les informations a jour et exactes, mais ne le garantissons pas. L'utilisation se fait aux frais et risques de l'Utilisateur (voir egalement article 12).

---

## 4. Rendez-vous & prestataires de soins externes

- L'App peut, maintenant ou a l'avenir, faciliter la prise de rendez-vous avec des prestataires de soins externes (comme Vaccinatiepunt). Pour la planification, il peut etre necessaire que nous partagions, uniquement avec ton consentement explicite, des donnees (nom, coordonnees, vaccination souhaitee) avec le prestataire concerne.
- Apres transfert au prestataire de soins, cette partie traite les donnees comme responsable independant selon ses propres conditions de confidentialite. Lis toujours ces conditions attentivement.
- Sans consentement, aucun partage de donnees n'a lieu et aucun rendez-vous ne peut etre reserve via l'App.

---

## 5. Regles d'utilisation

- Utilise l'App conformement aux presentes conditions et a la loi applicable. Sont notamment interdits : usage illicite, trompeur ou offensant ; atteinte aux droits de PI ; perturbation de l'App (par ex. malware, scraping) ; contournement de la securite.
- En cas de violation soupconnee ou averee, nous pouvons prendre des mesures appropriees, notamment blocage temporaire du Compte/de l'acces et suppression de contenu illicite.
- Nous pouvons cooperer avec les demandes legales des autorites competentes et, dans les conditions prevues par la loi, fournir des donnees d'identification en cas d'illiceite manifeste.

---

## 6. Services payants & abonnements

- Certaines fonctions sont payantes (par ex. Premium). Les prix et conditions sont affiches a l'avance. Les achats integres passent par Apple/Google ; la confirmation par le systeme de la boutique est requise.
- Les abonnements peuvent se renouveler automatiquement selon les conditions de la boutique d'applications. La gestion et la resiliation se font via les parametres de ton compte de boutique. Les periodes d'essai et promotions eventuelles sont indiquees a l'avance.
- Dans la mesure permise par la loi, il n'existe pas de delai de reflexion/droit de retractation pour le contenu numerique deja fourni via l'App Store/Google Play (les regles de la boutique prevalent).

---

## 7. Propriete intellectuelle & licence

- Tous les droits de PI sur l'App, le contenu et la technologie sous-jacente appartiennent a Vaccinatieboekje ou a ses concédants. L'Utilisateur recoit un droit d'utilisation personnel, revocable, non exclusif et non transferable pour la duree du Contrat.
- Il est interdit de copier, vendre, louer, sous-licencier, faire de l'ingenierie inverse, decompiler ou contourner la securite de l'App ou de ses parties, sauf si le droit imperatif l'autorise.
- Les marques, logos et noms commerciaux (dont "Vaxy") sont proteges. Les mentions de droits de PI ou de confidentialite ne peuvent pas etre supprimees.
- En fournissant des retours/suggestions, tu nous accordes une licence gratuite, mondiale et transferable pour utiliser ces contributions afin d'ameliorer et developper l'App.

---

## 8. Securite & Confidentialite

- Nous prenons des mesures techniques et organisationnelles appropriees pour securiser l'App.
- Les donnees personnelles sont traitees conformement a notre declaration de confidentialite et relative aux cookies. Celle-ci ne fait pas partie des presentes conditions, mais s'applique a l'utilisation de l'App.
- Nous pouvons t'envoyer des e-mails de service necessaires a l'App, et t'inviter occasionnellement a une enquete volontaire sur l'App. Tu peux toujours refuser ou te desinscrire via le lien de desinscription. Les messages promotionnels ne sont envoyes qu'avec ton consentement. Voir la declaration de confidentialite.

---

## 9. Donnees de l'Utilisateur

- Les donnees que tu saisis restent ta propriete. Pour fournir l'App, tu nous accordes une licence limitee pour heberger, traiter et afficher ces donnees tant que ton Compte est actif et conformement a la declaration de confidentialite.
- Tu peux supprimer des donnees via l'App. En cas de resiliation, tu peux demander, dans la mesure techniquement raisonnable, une copie/export dans un format courant (voir aussi notre declaration de confidentialite pour les modalites et delais).
- Tu es responsable de l'exactitude, de la qualite et de la legalite des donnees saisies et nous garantis contre les reclamations de tiers qui en decoulent.

---

## 10. Disponibilite, maintenance & modifications

- Nous visons une disponibilite elevee mais ne garantissons pas un fonctionnement sans erreur ou ininterrompu. Maintenance, mises a jour ou pannes peuvent causer des interruptions temporaires.
- Nous pouvons modifier, ajouter ou supprimer des parties de l'App, y compris des fonctionnalites beta ou de test. Si une modification limite substantiellement la fonctionnalite essentielle pour les consommateurs, nous t'en informerons de maniere raisonnable.
- Les mises a jour passent par l'App Store/Google Play. Tu es responsable de leur installation ; ne pas les installer peut nuire au fonctionnement et a la securite.

---

## 11. Informations de tiers

- L'App peut contenir des liens ou integrations vers des sites/services de tiers (par ex. prestataires de soins, boutiques d'applications). Nous ne sommes pas responsables de leur contenu ni de leur disponibilite.

---

## 12. Responsabilite

- Pas d'avis medical : l'App est informative et administrative. Ne prends jamais de decisions medicales uniquement sur la base de l'App.
- Dans la mesure permise par la loi, nous excluons la responsabilite pour dommages indirects, consecutifs, perte de benefices, perte de donnees, atteinte a la reputation ou dommages dus a une mauvaise utilisation de l'App.
- Notre responsabilite totale pour dommage direct est, dans la mesure permise par la loi, limitee a (a) le montant verse par notre assureur responsabilite ou, si ce montant est inferieur, (b) EUR 100 par evenement (max. EUR 300 par an).
- Les limitations de responsabilite ne s'appliquent pas en cas d'intention ou d'imprudence consciente de nos dirigeants appartenant a la direction.
- Nous ne sommes pas responsables des defauts ou modifications de l'App Store/Google Play ou des plateformes de paiement.

---

## 13. Plaintes & support

- Nous prenons les questions/plaintes au serieux. Contacte-nous via l'App ou les coordonnees ci-dessous. Nous repondons des que possible et en tout cas dans les 14 jours.

---

## 14. Duree & resiliation

- Le Contrat dure tant que tu utilises l'App et/ou possedes un Compte. Tu peux resilier a tout moment en supprimant ton Compte et en desinstallant l'App.
- Nous pouvons resilier ou suspendre tout ou partie du Contrat si tu violes ces conditions ou la loi applicable, ou si des abus ou risques de securite existent selon des criteres objectifs.
- Apres resiliation, cesse immediatement toute utilisation. Exporte tes donnees au prealable lorsque c'est possible.

---

## 15. Modification de ces conditions

- Nous pouvons modifier ces conditions. Les modifications substantielles sont communiquees au moins 30 jours a l'avance (par ex. par e-mail ou notification in-app). L'utilisation de l'App apres la date d'entree en vigueur vaut acceptation des nouvelles conditions.
- Si tu n'acceptes pas une modification substantielle, tu peux resilier le Contrat gratuitement avant la date d'entree en vigueur.

---

## 16. Droit applicable & juridiction

- Les presentes conditions et le Contrat sont regis par le droit neerlandais. Ce choix de loi ne porte pas atteinte a la protection imperative des consommateurs de ton Etat de residence.
- Sauf disposition imperative contraire, les litiges relevent du tribunal competent d'Amsterdam.

---

## 17. Dispositions diverses

- Si une disposition est nulle/annulable, le reste demeure en vigueur ; nous remplacerons la disposition invalide par une disposition valide se rapprochant le plus de l'intention.
- "Par ecrit" inclut egalement l'e-mail, si l'origine et l'integrite sont suffisamment etablies.
- Nous pouvons transferer les droits et obligations du Contrat dans le cadre, par exemple, d'une restructuration ou du transfert de tout ou partie de notre entreprise, dans le respect du droit applicable.

---

## 18. Contact

Vaccinatieboekje B.V.  
Bilderdijkkade 48  
1053 GJ Amsterdam  
KvK 69402574  
E-mail : info@vaccinatieboekje.com`,
};

const termsEs: LegalDoc = {
  title: "Condiciones de uso",
  updated: "18-09-2025",
  markdown: `Vaxy es una aplicacion y nombre comercial de Vaccinatieboekje B.V.

Version: 2.0  
Fecha: 18-09-2025

Estas Condiciones de uso se aplican al uso de la aplicacion movil Vaxy y la aplicacion web y sitios web asociados (conjuntamente: la App), ofrecidos por Vaccinatieboekje B.V., con domicilio en Bilderdijkkade 48, 1053 GJ Amsterdam, inscrita en la KvK con el numero 69402574 (Vaccinatieboekje, nosotros).

---

## 1. Definiciones

En estas condiciones, los terminos con mayuscula inicial (singular y plural) tienen el siguiente significado:

- Cuenta: el perfil personal de usuario que el Usuario crea para utilizar la App (incluidos subcuentas/perfiles familiares).
- App: la aplicacion movil y web "Vaxy", propiedad de Vaccinatieboekje B.V.
- Usuario: la persona fisica que no actua en el ejercicio de una profesion o empresa y que utiliza la App o crea una Cuenta.
- Acuerdo: el acuerdo entre el Usuario y Vaccinatieboekje relativo al uso de la App, del que estas condiciones forman parte.
- Derechos de propiedad intelectual: todos los derechos de PI, incluidos derechos de autor, derechos sobre bases de datos, nombres comerciales, marcas, disenos, patentes y know-how.

---

## 2. Formalizacion y Cuenta

- La App puede descargarse desde Apple App Store y Google Play Store. Es necesario registrarse para utilizar partes de la App.
- El Acuerdo se formaliza al instalar la App y/o registrar la Cuenta.
- Si se aplican pagos dentro de la app, se indicara claramente con antelacion. Las compras se realizan mediante los sistemas de pago de la tienda correspondiente.
- El Usuario elige una contrasena segura y es responsable de mantener la confidencialidad de las credenciales. Podemos confiar en que las acciones realizadas a traves de la Cuenta han sido realizadas por el Usuario.
- El Usuario puede gestionar subcuentas/perfiles familiares. Al desvincularse, puede crearse una Cuenta independiente.

---

## 3. Funcionalidad de la App

- Con la App, el Usuario puede (a) consultar informacion sobre vacunas y enfermedades infecciosas y (b) registrar sus propias vacunas (por ejemplo dosis, fecha, marca, lote, validez) y guardar notas/fotos (por ejemplo de una cartilla en papel).
- Sin asesoramiento medico: la App es una libreta digital de vacunacion y servicio informativo. La App no ofrece asesoramiento medico, diagnostico ni tratamiento. Para preguntas medicas, vacunas de viaje y decisiones, el Usuario debe consultar siempre a un medico o institucion autorizada. El Usuario es responsable de la exactitud e integridad de sus propias entradas.
- El Usuario puede exportar resumenes (por ejemplo PDF) o recibirlos por correo electronico.
- Nos esforzamos por mantener la informacion actualizada y correcta, pero no lo garantizamos. El uso corre por cuenta y riesgo del Usuario (vease tambien articulo 12).

---

## 4. Citas y proveedores sanitarios externos

- La App puede ahora o en el futuro facilitar la concertacion de citas con proveedores sanitarios externos (como Vaccinatiepunt). Para programar, puede ser necesario que, solo con tu consentimiento explicito, compartamos datos (como nombre, datos de contacto, vacuna deseada) con el proveedor correspondiente.
- Tras la transferencia al proveedor sanitario, esa parte trata los datos como responsable independiente bajo sus propias condiciones de privacidad. Lee siempre esas condiciones con atencion.
- Si no das consentimiento, no se comparten datos y no puede reservarse una cita a traves de la App.

---

## 5. Reglas de uso

- Utiliza la App de conformidad con estas condiciones y la legislacion aplicable. Estan prohibidos, entre otros: uso ilicito, enganoso u ofensivo; infraccion de derechos de PI; perturbacion de la App (por ejemplo malware, scraping); o eludir la seguridad.
- En caso de infraccion sospechada o real, podemos adoptar medidas adecuadas, incluido bloqueo temporal de Cuenta/acceso y eliminacion de contenido ilicito.
- Podemos cooperar con solicitudes legales de autoridades competentes y, bajo las condiciones legales, proporcionar datos identificativos en caso de ilicitud evidente.

---

## 6. Servicios de pago y suscripciones

- Algunas funciones son de pago (por ejemplo Premium). Los precios y condiciones se muestran previamente. Las compras dentro de la app se realizan mediante Apple/Google; se requiere confirmacion del sistema de la tienda.
- Las suscripciones pueden renovarse automaticamente segun las condiciones de la tienda. La gestion y cancelacion se realizan desde la configuracion de tu cuenta de tienda. Los periodos de prueba y promociones se indican previamente.
- En la medida permitida por la ley, no hay periodo de desistimiento/resolucion para contenido digital ya entregado via App Store/Google Play (prevalecen las normas de la tienda).

---

## 7. Propiedad intelectual y licencia

- Todos los derechos de PI sobre la App, el contenido y la tecnologia subyacente pertenecen a Vaccinatieboekje o sus licenciantes. El Usuario obtiene un derecho de uso personal, revocable, no exclusivo e intransferible durante la duracion del Acuerdo.
- No esta permitido copiar, vender, alquilar, sublicenciar, realizar ingenieria inversa, descompilar o eludir la seguridad de la App o partes de ella, salvo que la ley imperativa lo permita.
- Las marcas, logotipos y nombres comerciales (incluido "Vaxy") estan protegidos. No pueden eliminarse menciones de derechos de PI o confidencialidad.
- Al proporcionar comentarios/sugerencias, nos concedes una licencia gratuita, mundial y transferible para utilizar dichas aportaciones con el fin de mejorar y desarrollar la App.

---

## 8. Seguridad y privacidad

- Adoptamos medidas tecnicas y organizativas adecuadas para proteger la App.
- Los datos personales se tratan conforme a nuestra declaracion de privacidad y cookies. Esta no forma parte de estas condiciones, pero se aplica al uso de la App.
- Podemos enviarte correos de servicio necesarios para la App e invitarte ocasionalmente a una encuesta voluntaria sobre la App. Siempre puedes rechazarla o darte de baja mediante el enlace de baja. Los mensajes promocionales solo se envian con tu consentimiento. Consulta la declaracion de privacidad.

---

## 9. Datos del Usuario

- Los datos que introduces siguen siendo de tu propiedad. Para poder prestar la App, nos concedes una licencia limitada para alojar, tratar y mostrar estos datos mientras tu Cuenta este activa y conforme a la declaracion de privacidad.
- Puedes eliminar datos mediante la App. Al finalizar, puedes solicitar, en la medida tecnicamente razonable, una copia/exportacion en un formato comun (vease tambien nuestra declaracion de privacidad para modalidades y plazos).
- Eres responsable de la exactitud, calidad y legalidad de los datos introducidos y nos indemnizas frente a reclamaciones de terceros derivadas de ellos.

---

## 10. Disponibilidad, mantenimiento y cambios

- Aspiramos a una alta disponibilidad, pero no garantizamos un funcionamiento sin errores o ininterrumpido. Mantenimiento, actualizaciones o fallos pueden causar interrupciones temporales.
- Podemos modificar, anadir o eliminar partes de la App, incluidas funcionalidades beta o de prueba. Si un cambio limita sustancialmente la funcionalidad principal para consumidores, te informaremos de forma razonable.
- Las actualizaciones se realizan via App Store/Google Play. Eres responsable de instalarlas; no hacerlo puede afectar negativamente al funcionamiento y la seguridad.

---

## 11. Informacion de terceros

- La App puede contener enlaces o integraciones con sitios/servicios de terceros (por ejemplo proveedores sanitarios, tiendas de aplicaciones). No somos responsables de su contenido ni de su disponibilidad.

---

## 12. Responsabilidad

- Sin asesoramiento medico: la App es informativa y administrativa. Nunca tomes decisiones medicas exclusivamente basandote en la App.
- En la medida permitida por la ley, excluimos responsabilidad por danos indirectos, consecuenciales, lucro cesante, perdida de datos, dano reputacional o danos por uso incorrecto de la App.
- Nuestra responsabilidad total por danos directos esta, en la medida permitida por la ley, limitada a (a) el importe que pague nuestro seguro de responsabilidad o, si es inferior, (b) EUR 100 por evento (max. EUR 300 por ano).
- Las limitaciones de responsabilidad no se aplican en caso de dolo o imprudencia consciente de nuestros directivos pertenecientes a la direccion.
- No somos responsables de defectos o cambios en App Store/Google Play o plataformas de pago.

---

## 13. Reclamaciones y soporte

- Tratamos las preguntas/reclamaciones seriamente. Contactanos via la App o los datos de contacto siguientes. Respondemos lo antes posible y en todo caso dentro de 14 dias.

---

## 14. Duracion y terminacion

- El Acuerdo dura mientras uses la App y/o tengas una Cuenta. Puedes terminarlo en cualquier momento eliminando tu Cuenta y desinstalando la App.
- Podemos terminar o suspender total o parcialmente el Acuerdo si infringes estas condiciones o la ley aplicable, o si existen abuso o riesgos de seguridad segun criterios objetivos.
- Tras la terminacion, deja de usar la App inmediatamente. Exporta antes tus datos cuando sea posible.

---

## 15. Modificacion de estas condiciones

- Podemos modificar estas condiciones. Comunicamos los cambios sustanciales al menos 30 dias antes (por ejemplo por e-mail o aviso in-app). Usar la App tras la fecha de entrada en vigor implica aceptar las nuevas condiciones.
- Si no aceptas un cambio material, puedes terminar el Acuerdo gratuitamente antes de la fecha de entrada en vigor.

---

## 16. Derecho aplicable y foro

- Estas condiciones y el Acuerdo se rigen por el derecho neerlandes. Esta eleccion no afecta a la proteccion imperativa de consumidores de tu Estado de residencia.
- Salvo que la ley imperativa disponga otra cosa, las disputas se someten al tribunal competente de Amsterdam.

---

## 17. Otras disposiciones

- Si una disposicion es nula/anulable, el resto seguira vigente; sustituiremos la disposicion invalida por una valida que se aproxime lo mas posible a la intencion.
- "Por escrito" incluye tambien el correo electronico, siempre que el origen y la integridad esten suficientemente establecidos.
- Podemos transferir derechos y obligaciones del Acuerdo en el marco, por ejemplo, de una reestructuracion o transferencia de todo o parte de nuestra empresa, respetando la ley aplicable.

---

## 18. Contacto

Vaccinatieboekje B.V.  
Bilderdijkkade 48  
1053 GJ Amsterdam  
KvK 69402574  
Correo electronico: info@vaccinatieboekje.com`,
};

const faqNl: FaqDoc = {
  title: "Veelgestelde vragen",
  intro:
    "Sommige vragen over Vaxy krijgen we vaak. De belangrijkste vind je hieronder met onze antwoorden.",
  groups: [
    {
      heading: "Algemeen",
      items: [
        {
          q: "Waarom zou ik Vaxy gebruiken?",
          a: "Veel mensen raken hun vaccinatiebewijzen kwijt of hebben geen volledig overzicht van hun vaccinaties. Vaxy lost dit op door al je vaccinaties veilig en overzichtelijk op te slaan, inclusief die van je gezin. Je krijgt herinneringen wanneer een vaccinatie bijna verloopt en je kunt bewijs eenvoudig bewaren of delen. Handig bij doktersbezoek of als je op reis gaat.",
        },
        {
          q: "Heb ik een vaccinatieboekje nodig?",
          a: "Je hebt een vaccinatieboekje alleen nodig wanneer je reist naar landen waar dit verplicht is. Een digitale back-up is verstandig voor het geval je het boekje kwijtraakt. Voor gele koorts kan bewijs verplicht zijn in onder meer Angola, Benin, Burkina Faso, Burundi, Kameroen, Centraal-Afrikaanse Republiek, Congo, Democratische Republiek Congo, Côte d'Ivoire (Ivoorkust), Equatoriaal-Guinea, Gabon, Gambia, Ghana, Guinee, Guinee-Bissau, Kenia, Liberia, Mali, Mauritanië, Niger, Nigeria, Senegal, Sierra Leone, Zuid-Soedan, Togo en Oeganda; in Zuid-Amerika voor Bolivia en Frans-Guyana. Ook landen zoals Suriname, Brazilië, Ecuador, Colombia, Peru, Venezuela, Guyana, Paraguay, Panama, India, Australië, Singapore, China, Verenigde Arabische Emiraten en Saoedi-Arabië kunnen een bewijs vragen als je uit een gelekoortsrisicoland komt of daar hebt doorgereisd. Regels verschillen per land en veranderen regelmatig; controleer dit voor vertrek via ggdreisvaccinaties.nl of een officiële reiskliniek.",
        },
        {
          q: "Waar kan ik mijn vaccinatiegeschiedenis opvragen?",
          a: "Je kunt een deel van je vaccinaties bekijken via mijn.rivm.nl door in te loggen met DigiD, maar niet alles staat daar altijd geregistreerd. Vaccinaties van kinderen staan vaak in een zorgportaal zoals Mijn kinddossier of GGD Ouderportaal. Reisvaccinaties worden vaak per kliniek geregistreerd en kun je daar opvragen. Vaccinaties die door je huisarts zijn gegeven kun je bij je huisarts opvragen. Je kunt vaccinaties daarna gemakkelijk importeren door een foto te nemen; Vaxy herkent ze automatisch.",
        },
        {
          q: "Kan ik ook een afspraak maken voor reisvaccinaties via de app?",
          a: "Ja, je kunt via de app een afspraak maken voor je reisvaccinaties.",
        },
      ],
    },
    {
      heading: "Betrouwbaarheid & veiligheid",
      items: [
        {
          q: "Help, mijn gegevens zijn weg. Hoe kan dat?",
          a: "Je gegevens worden door ons veilig opgeslagen en nooit door ons verwijderd. Soms vergeten gebruikers welke inlogmethode ze hebben gebruikt, zoals Apple, Google, Facebook of e-mail. In dat geval vinden wij het account terug. Heb je een vraag over je data? Mail naar info@vaxy.app.",
        },
        {
          q: "Hoe waarborgt Vaxy de privacy van medische gegevens?",
          a: "Wij hanteren dataminimalisatie: we slaan alleen gegevens op die strikt noodzakelijk zijn voor de werking van de app. Medische gegevens zijn afgeschermd, worden nooit commercieel ingezet en worden niet gedeeld met derden zonder expliciete toestemming. Het doel is altijd dat jij controle houdt over je eigen gegevens.",
        },
        {
          q: "Welke beveiligingsmaatregelen gebruiken jullie?",
          a: "We volgen internationale best practices en beveiligingsstandaarden. Dat betekent onder meer versleuteling van data tijdens opslag en transport, beperkte en gecontroleerde toegang tot servers, continue monitoring en logging om afwijkingen snel te detecteren, en regelmatige updates en beveiligingstesten om nieuwe risico's te voorkomen. We evalueren onze maatregelen periodiek en passen ze aan wanneer nieuwe technieken of risico's zich voordoen.",
        },
        {
          q: "Wie kan de medische gegevens inzien?",
          a: "Alleen strikt geautoriseerde personen hebben toegang, en uitsluitend voor noodzakelijke technische werkzaamheden of doorontwikkeling van de app. Niemand anders kan jouw gegevens inzien of gebruiken. Jij blijft altijd eigenaar van je gegevens.",
        },
        {
          q: "Hoe zorgen jullie dat de app betrouwbaar blijft?",
          a: "De app is ontworpen om veilig en stabiel te functioneren. We voeren doorlopend verbeteringen en updates door, testen elke nieuwe versie uitgebreid en werken samen om hoge kwaliteitsstandaarden te waarborgen.",
        },
      ],
    },
    {
      heading: "Gebruik & functies",
      items: [
        {
          q: "Welke functies biedt de app?",
          a: "De belangrijkste functies zijn: vaccinaties registreren met merk, batchnummer, dosis en geldigheid; herinneringen ontvangen wanneer een vaccinatie bijna verloopt; vaccinatiebewijzen uploaden of fotograferen voor je eigen archief; informatie bekijken over infectieziekten en het Rijksvaccinatieprogramma; je vaccinatieoverzicht exporteren, bijvoorbeeld als PDF; en meerdere profielen beheren, bijvoorbeeld voor kinderen of familieleden.",
        },
        {
          q: "Waarvoor kan ik Vaxy gebruiken?",
          a: "Met Vaxy houd je al je vaccinaties overzichtelijk bij in één app. Je kunt vaccinaties registreren, herinneringen instellen en informatie over infectieziekten raadplegen. Zo heb je altijd inzicht in je eigen vaccinaties en die van je kinderen.",
        },
        {
          q: "Worden er nieuwe functies toegevoegd?",
          a: "Ja. We werken continu aan uitbreiding van de app, zoals koppelingen met zorginstellingen, de mogelijkheid om afspraken te maken bij aangesloten priklocaties en extra inzichten op basis van anonieme trends en statistieken. Gebruikersfeedback speelt een grote rol bij het ontwikkelen van nieuwe functies.",
        },
        {
          q: "Kan ik zelf infectieziekten toevoegen in de app?",
          a: "Nee, dat kan niet rechtstreeks. Mis je iets? Stuur ons een e-mail via info@vaxy.app, dan beoordelen we of we het kunnen toevoegen.",
        },
      ],
    },
    {
      heading: "Overige",
      items: [
        {
          q: "Hoe kan ik contact met jullie opnemen?",
          a: "Via het feedbackformulier in de app of per mail naar info@vaxy.app. We reageren meestal binnen één werkdag.",
        },
        {
          q: "Kan ik mijn gegevens exporteren of meenemen naar een andere app?",
          a: "Ja. Je kunt je vaccinatieoverzicht exporteren, bijvoorbeeld als PDF. We onderzoeken daarnaast mogelijkheden voor koppelingen met andere systemen, zodat jouw gegevens bruikbaar blijven.",
        },
        {
          q: "Wat als ik mijn account wil verwijderen?",
          a: "Je kunt je account op ieder moment verwijderen via de instellingen. Alle persoonsgegevens worden dan gewist. Geanonimiseerde gegevens die niet tot jou te herleiden zijn kunnen we bewaren voor statistische doeleinden, bijvoorbeeld om trends te tonen.",
        },
      ],
    },
  ],
};

const faqEn: FaqDoc = {
  title: "Frequently Asked Questions",
  intro:
    "We often receive certain questions about Vaxy. You will find the most important ones below with our answers.",
  groups: [
    {
      heading: "General",
      items: [
        {
          q: "Why should I use Vaxy?",
          a: "Many people lose their vaccination records or do not have a complete overview of their vaccinations. Vaxy solves this by storing all your vaccinations securely and clearly, including those of your family. You receive reminders when a vaccination is about to expire and can easily keep or share proof. This is useful for doctor visits or travel.",
        },
        {
          q: "Do I need a vaccination booklet?",
          a: "You only need a vaccination booklet when travelling to countries where it is mandatory. A digital backup is wise in case you lose the booklet. For yellow fever, proof may be mandatory in countries including Angola, Benin, Burkina Faso, Burundi, Cameroon, Central African Republic, Congo, Democratic Republic of the Congo, Côte d'Ivoire (Ivory Coast), Equatorial Guinea, Gabon, Gambia, Ghana, Guinea, Guinea-Bissau, Kenya, Liberia, Mali, Mauritania, Niger, Nigeria, Senegal, Sierra Leone, South Sudan, Togo and Uganda; in South America this applies to Bolivia and French Guiana. Countries such as Suriname, Brazil, Ecuador, Colombia, Peru, Venezuela, Guyana, Paraguay, Panama, India, Australia, Singapore, China, the United Arab Emirates and Saudi Arabia may also require proof if you arrive from or travelled through a yellow-fever risk country. Rules differ per country and change regularly; check before departure via ggdreisvaccinaties.nl or an official travel clinic.",
        },
        {
          q: "Where can I request my vaccination history?",
          a: "You can view part of your vaccinations via mijn.rivm.nl by logging in with DigiD, but not everything is always registered there. Children's vaccinations are often recorded in a care portal such as Mijn kinddossier or GGD Ouderportaal. Travel vaccinations are often registered per clinic and can be requested there. Vaccinations given by your GP can be requested from your GP. You can then import vaccinations easily by taking a photo; Vaxy recognizes them automatically.",
        },
        {
          q: "Can I make an appointment for travel vaccinations through the app?",
          a: "Yes, you can make an appointment for your travel vaccinations through the app.",
        },
      ],
    },
    {
      heading: "Reliability & security",
      items: [
        {
          q: "Help, my data is gone. How is that possible?",
          a: "Your data is stored securely by us and is never deleted by us. Sometimes users forget which login method they used, such as Apple, Google, Facebook or email. In that case we can find the account. If you have a question about your data, email info@vaxy.app.",
        },
        {
          q: "How does Vaxy protect the privacy of medical data?",
          a: "We apply data minimization: we store only the data strictly necessary for the app to work. Medical data is shielded, is never used commercially and is not shared with third parties without explicit consent. The goal is always to give you control over your own data.",
        },
        {
          q: "Which security measures do you use?",
          a: "We follow international best practices and security standards. This includes encryption of data at rest and in transit, limited and controlled server access, continuous monitoring and logging to quickly detect anomalies, and regular updates and security tests to prevent new risks. We periodically evaluate our measures and adapt them when new techniques or risks arise.",
        },
        {
          q: "Who can view the medical data?",
          a: "Only strictly authorized persons have access, and only for necessary technical work or further development of the app. No one else can view or use your data. You always remain the owner of your data.",
        },
        {
          q: "How do you keep the app reliable?",
          a: "The app is designed to function safely and stably. We continuously make improvements and updates, extensively test every new version and work together to maintain high quality standards.",
        },
      ],
    },
    {
      heading: "Use & features",
      items: [
        {
          q: "Which features does the app offer?",
          a: "The main features are: registering vaccinations with brand, batch number, dose and validity; receiving reminders when a vaccination is about to expire; uploading or photographing vaccination records for your own archive; viewing information about infectious diseases and the Dutch National Immunization Program; exporting your vaccination overview, for example as a PDF; and managing multiple profiles, for example for children or family members.",
        },
        {
          q: "What can I use Vaxy for?",
          a: "With Vaxy you keep all your vaccinations clearly organized in one app. You can register vaccinations, set reminders and consult information about infectious diseases. This gives you insight into your own vaccinations and those of your children.",
        },
        {
          q: "Will new features be added?",
          a: "Yes. We continuously work on expanding the app, such as integrations with healthcare institutions, the option to make appointments at connected vaccination locations and additional insights based on anonymous trends and statistics. User feedback plays a major role in developing new features.",
        },
        {
          q: "Can I add infectious diseases myself in the app?",
          a: "No, not directly. Missing something? Send us an email at info@vaxy.app and we will assess whether we can add it.",
        },
      ],
    },
    {
      heading: "Other",
      items: [
        {
          q: "How can I contact you?",
          a: "Via the feedback form in the app or by email at info@vaxy.app. We usually respond within one working day.",
        },
        {
          q: "Can I export my data or take it to another app?",
          a: "Yes. You can export your vaccination overview, for example as a PDF. We are also exploring options for integrations with other systems so your data remains usable.",
        },
        {
          q: "What if I want to delete my account?",
          a: "You can delete your account at any time through the settings. All personal data will then be erased. Anonymized data that cannot be traced back to you may be kept for statistical purposes, for example to show trends.",
        },
      ],
    },
  ],
};

const faqDe: FaqDoc = {
  title: "Haeufig gestellte Fragen",
  intro:
    "Einige Fragen zu Vaxy erhalten wir haeufig. Die wichtigsten findest du unten mit unseren Antworten.",
  groups: [
    {
      heading: "Allgemein",
      items: [
        {
          q: "Warum sollte ich Vaxy verwenden?",
          a: "Viele Menschen verlieren ihre Impfnachweise oder haben keinen vollstaendigen Ueberblick ueber ihre Impfungen. Vaxy loest dies, indem alle deine Impfungen sicher und uebersichtlich gespeichert werden, einschliesslich der deiner Familie. Du erhaeltst Erinnerungen, wenn eine Impfung bald ablaeuft, und kannst Nachweise einfach aufbewahren oder teilen. Praktisch beim Arztbesuch oder auf Reisen.",
        },
        {
          q: "Brauche ich ein Impfbuch?",
          a: "Ein Impfbuch brauchst du nur, wenn du in Laender reist, in denen es vorgeschrieben ist. Eine digitale Sicherung ist sinnvoll, falls du das Heft verlierst. Fuer Gelbfieber kann ein Nachweis unter anderem in Angola, Benin, Burkina Faso, Burundi, Kamerun, Zentralafrikanische Republik, Kongo, Demokratische Republik Kongo, Côte d'Ivoire (Elfenbeinkueste), Aequatorialguinea, Gabun, Gambia, Ghana, Guinea, Guinea-Bissau, Kenia, Liberia, Mali, Mauretanien, Niger, Nigeria, Senegal, Sierra Leone, Suedsudan, Togo und Uganda vorgeschrieben sein; in Suedamerika gilt dies fuer Bolivien und Franzoesisch-Guayana. Auch Laender wie Suriname, Brasilien, Ecuador, Kolumbien, Peru, Venezuela, Guyana, Paraguay, Panama, Indien, Australien, Singapur, China, Vereinigte Arabische Emirate und Saudi-Arabien koennen einen Nachweis verlangen, wenn du aus einem Gelbfieber-Risikogebiet kommst oder dort durchgereist bist. Regeln unterscheiden sich je Land und aendern sich regelmaessig; pruefe sie vor der Abreise ueber ggdreisvaccinaties.nl oder eine offizielle Reiseklinik.",
        },
        {
          q: "Wo kann ich meine Impfgeschichte anfordern?",
          a: "Einen Teil deiner Impfungen kannst du ueber mijn.rivm.nl mit DigiD einsehen, aber nicht alles ist dort immer registriert. Impfungen von Kindern stehen oft in einem Gesundheitsportal wie Mijn kinddossier oder GGD Ouderportaal. Reiseimpfungen werden haeufig je Klinik registriert und koennen dort angefordert werden. Impfungen durch deinen Hausarzt kannst du beim Hausarzt anfordern. Danach kannst du Impfungen einfach importieren, indem du ein Foto machst; Vaxy erkennt sie automatisch.",
        },
        {
          q: "Kann ich ueber die App einen Termin fuer Reiseimpfungen vereinbaren?",
          a: "Ja, du kannst ueber die App einen Termin fuer deine Reiseimpfungen vereinbaren.",
        },
      ],
    },
    {
      heading: "Zuverlaessigkeit & Sicherheit",
      items: [
        {
          q: "Hilfe, meine Daten sind weg. Wie kann das sein?",
          a: "Deine Daten werden von uns sicher gespeichert und niemals von uns geloescht. Manchmal vergessen Nutzer, welche Anmeldemethode sie verwendet haben, etwa Apple, Google, Facebook oder E-Mail. In diesem Fall finden wir das Konto wieder. Bei Fragen zu deinen Daten schreibe an info@vaxy.app.",
        },
        {
          q: "Wie gewaehrleistet Vaxy den Datenschutz medizinischer Daten?",
          a: "Wir wenden Datenminimierung an: Wir speichern nur Daten, die fuer den Betrieb der App strikt notwendig sind. Medizinische Daten sind abgeschirmt, werden niemals kommerziell genutzt und nicht ohne ausdrueckliche Einwilligung an Dritte weitergegeben. Ziel ist immer, dir Kontrolle ueber deine eigenen Daten zu geben.",
        },
        {
          q: "Welche Sicherheitsmassnahmen nutzt ihr?",
          a: "Wir folgen internationalen Best Practices und Sicherheitsstandards. Dazu gehoeren Verschluesselung von Daten bei Speicherung und Transport, beschraenkter und kontrollierter Serverzugang, kontinuierliches Monitoring und Logging zur schnellen Erkennung von Abweichungen sowie regelmaessige Updates und Sicherheitstests zur Vermeidung neuer Risiken. Wir bewerten unsere Massnahmen regelmaessig und passen sie bei neuen Techniken oder Risiken an.",
        },
        {
          q: "Wer kann die medizinischen Daten einsehen?",
          a: "Nur streng autorisierte Personen haben Zugriff, und ausschliesslich fuer notwendige technische Arbeiten oder die Weiterentwicklung der App. Niemand sonst kann deine Daten einsehen oder verwenden. Du bleibst immer Eigentuemer deiner Daten.",
        },
        {
          q: "Wie sorgt ihr dafuer, dass die App zuverlaessig bleibt?",
          a: "Die App ist darauf ausgelegt, sicher und stabil zu funktionieren. Wir fuehren laufend Verbesserungen und Updates durch, testen jede neue Version umfassend und arbeiten zusammen, um hohe Qualitaetsstandards zu sichern.",
        },
      ],
    },
    {
      heading: "Nutzung & Funktionen",
      items: [
        {
          q: "Welche Funktionen bietet die App?",
          a: "Die wichtigsten Funktionen sind: Impfungen mit Marke, Chargennummer, Dosis und Gueltigkeit registrieren; Erinnerungen erhalten, wenn eine Impfung bald ablaeuft; Impfnachweise fuer dein eigenes Archiv hochladen oder fotografieren; Informationen ueber Infektionskrankheiten und das niederlaendische Rijksvaccinatieprogramma ansehen; deine Impfuebersicht zum Beispiel als PDF exportieren; und mehrere Profile verwalten, etwa fuer Kinder oder Familienmitglieder.",
        },
        {
          q: "Wofuer kann ich Vaxy verwenden?",
          a: "Mit Vaxy verwaltest du alle deine Impfungen uebersichtlich in einer App. Du kannst Impfungen registrieren, Erinnerungen einstellen und Informationen ueber Infektionskrankheiten abrufen. So hast du immer Einblick in deine eigenen Impfungen und die deiner Kinder.",
        },
        {
          q: "Werden neue Funktionen hinzugefuegt?",
          a: "Ja. Wir arbeiten kontinuierlich an der Erweiterung der App, etwa an Verbindungen mit Gesundheitseinrichtungen, der Moeglichkeit, Termine bei angeschlossenen Impfstellen zu buchen, und zusaetzlichen Einblicken auf Basis anonymer Trends und Statistiken. Nutzerfeedback spielt eine grosse Rolle bei der Entwicklung neuer Funktionen.",
        },
        {
          q: "Kann ich selbst Infektionskrankheiten in der App hinzufuegen?",
          a: "Nein, nicht direkt. Fehlt dir etwas? Sende uns eine E-Mail an info@vaxy.app, dann pruefen wir, ob wir es hinzufuegen koennen.",
        },
      ],
    },
    {
      heading: "Sonstiges",
      items: [
        {
          q: "Wie kann ich euch kontaktieren?",
          a: "Ueber das Feedbackformular in der App oder per E-Mail an info@vaxy.app. Wir antworten meist innerhalb eines Arbeitstags.",
        },
        {
          q: "Kann ich meine Daten exportieren oder in eine andere App mitnehmen?",
          a: "Ja. Du kannst deine Impfuebersicht zum Beispiel als PDF exportieren. Ausserdem pruefen wir Moeglichkeiten fuer Verbindungen mit anderen Systemen, damit deine Daten nutzbar bleiben.",
        },
        {
          q: "Was, wenn ich mein Konto loeschen moechte?",
          a: "Du kannst dein Konto jederzeit ueber die Einstellungen loeschen. Alle personenbezogenen Daten werden dann geloescht. Anonymisierte Daten, die nicht auf dich zurueckfuehrbar sind, koennen wir fuer statistische Zwecke aufbewahren, zum Beispiel um Trends zu zeigen.",
        },
      ],
    },
  ],
};

const faqFr: FaqDoc = {
  title: "Questions frequentes",
  intro:
    "Certaines questions sur Vaxy reviennent souvent. Tu trouveras ci-dessous les principales, avec nos reponses.",
  groups: [
    {
      heading: "General",
      items: [
        {
          q: "Pourquoi utiliser Vaxy ?",
          a: "Beaucoup de personnes perdent leurs preuves de vaccination ou n'ont pas de vue complete de leurs vaccinations. Vaxy resout cela en stockant toutes tes vaccinations de facon sure et claire, y compris celles de ta famille. Tu recois des rappels lorsqu'une vaccination arrive bientot a expiration et tu peux conserver ou partager facilement une preuve. C'est pratique lors d'une visite medicale ou d'un voyage.",
        },
        {
          q: "Ai-je besoin d'un carnet de vaccination ?",
          a: "Tu n'as besoin d'un carnet de vaccination que si tu voyages vers des pays ou il est obligatoire. Une sauvegarde numerique est utile si tu perds le carnet. Pour la fievre jaune, une preuve peut etre obligatoire notamment en Angola, Benin, Burkina Faso, Burundi, Cameroun, Republique centrafricaine, Congo, Republique democratique du Congo, Côte d'Ivoire, Guinee equatoriale, Gabon, Gambie, Ghana, Guinee, Guinee-Bissau, Kenya, Liberia, Mali, Mauritanie, Niger, Nigeria, Senegal, Sierra Leone, Soudan du Sud, Togo et Ouganda ; en Amerique du Sud, cela concerne la Bolivie et la Guyane francaise. Des pays comme le Suriname, le Bresil, l'Equateur, la Colombie, le Perou, le Venezuela, le Guyana, le Paraguay, le Panama, l'Inde, l'Australie, Singapour, la Chine, les Emirats arabes unis et l'Arabie saoudite peuvent aussi demander une preuve si tu arrives d'un pays a risque de fievre jaune ou y as transite. Les regles different selon les pays et changent regulierement ; verifie avant le depart via ggdreisvaccinaties.nl ou une clinique de voyage officielle.",
        },
        {
          q: "Ou puis-je demander mon historique vaccinal ?",
          a: "Tu peux consulter une partie de tes vaccinations via mijn.rivm.nl en te connectant avec DigiD, mais tout n'y est pas toujours enregistre. Les vaccinations des enfants sont souvent enregistrees dans un portail de soins comme Mijn kinddossier ou GGD Ouderportaal. Les vaccinations de voyage sont souvent enregistrees par clinique et peuvent y etre demandees. Les vaccinations faites par ton medecin generaliste peuvent etre demandees a celui-ci. Tu peux ensuite importer facilement les vaccinations en prenant une photo ; Vaxy les reconnait automatiquement.",
        },
        {
          q: "Puis-je aussi prendre rendez-vous pour des vaccinations de voyage via l'application ?",
          a: "Oui, tu peux prendre rendez-vous pour tes vaccinations de voyage via l'application.",
        },
      ],
    },
    {
      heading: "Fiabilite & securite",
      items: [
        {
          q: "A l'aide, mes donnees ont disparu. Comment est-ce possible ?",
          a: "Tes donnees sont stockees de maniere securisee par nos soins et ne sont jamais supprimees par nous. Il arrive que des utilisateurs oublient la methode de connexion utilisee, comme Apple, Google, Facebook ou e-mail. Dans ce cas, nous retrouvons le compte. Pour toute question sur tes donnees, ecris a info@vaxy.app.",
        },
        {
          q: "Comment Vaxy garantit-il la confidentialite des donnees medicales ?",
          a: "Nous appliquons la minimisation des donnees : nous ne stockons que les donnees strictement necessaires au fonctionnement de l'application. Les donnees medicales sont protegees, ne sont jamais utilisees commercialement et ne sont pas partagees avec des tiers sans consentement explicite. L'objectif est toujours de te donner le controle de tes propres donnees.",
        },
        {
          q: "Quelles mesures de securite utilisez-vous ?",
          a: "Nous suivons les meilleures pratiques internationales et les normes de securite. Cela comprend le chiffrement des donnees au repos et en transit, un acces limite et controle aux serveurs, une surveillance et une journalisation continues pour detecter rapidement les anomalies, ainsi que des mises a jour et tests de securite reguliers pour prevenir de nouveaux risques. Nous evaluons regulierement nos mesures et les adaptons lorsque de nouvelles techniques ou de nouveaux risques apparaissent.",
        },
        {
          q: "Qui peut consulter les donnees medicales ?",
          a: "Seules des personnes strictement autorisees y ont acces, et uniquement pour des travaux techniques necessaires ou le developpement de l'application. Personne d'autre ne peut consulter ou utiliser tes donnees. Tu restes toujours proprietaire de tes donnees.",
        },
        {
          q: "Comment veillez-vous a ce que l'application reste fiable ?",
          a: "L'application est concue pour fonctionner de maniere sure et stable. Nous apportons en continu des ameliorations et mises a jour, testons largement chaque nouvelle version et travaillons ensemble pour garantir des standards de qualite eleves.",
        },
      ],
    },
    {
      heading: "Utilisation & fonctionnalites",
      items: [
        {
          q: "Quelles fonctionnalites l'application offre-t-elle ?",
          a: "Les principales fonctionnalites sont : enregistrer les vaccinations avec marque, numero de lot, dose et validite ; recevoir des rappels lorsqu'une vaccination arrive bientot a expiration ; televerser ou photographier des preuves de vaccination pour tes archives ; consulter des informations sur les maladies infectieuses et le programme national neerlandais de vaccination ; exporter ton apercu vaccinal, par exemple en PDF ; et gerer plusieurs profils, par exemple pour des enfants ou membres de la famille.",
        },
        {
          q: "A quoi puis-je utiliser Vaxy ?",
          a: "Avec Vaxy, tu gardes toutes tes vaccinations clairement organisees dans une seule application. Tu peux enregistrer des vaccinations, configurer des rappels et consulter des informations sur les maladies infectieuses. Tu gardes ainsi une vue sur tes propres vaccinations et celles de tes enfants.",
        },
        {
          q: "De nouvelles fonctionnalites seront-elles ajoutees ?",
          a: "Oui. Nous travaillons continuellement a l'extension de l'application, par exemple avec des connexions a des etablissements de soins, la possibilite de prendre rendez-vous dans des lieux de vaccination connectes et des informations supplementaires basees sur des tendances et statistiques anonymes. Les retours utilisateurs jouent un role important dans le developpement de nouvelles fonctionnalites.",
        },
        {
          q: "Puis-je ajouter moi-meme des maladies infectieuses dans l'application ?",
          a: "Non, pas directement. Il manque quelque chose ? Envoie-nous un e-mail a info@vaxy.app et nous evaluerons si nous pouvons l'ajouter.",
        },
      ],
    },
    {
      heading: "Autres",
      items: [
        {
          q: "Comment puis-je vous contacter ?",
          a: "Via le formulaire de retour dans l'application ou par e-mail a info@vaxy.app. Nous repondons generalement dans un delai d'un jour ouvrable.",
        },
        {
          q: "Puis-je exporter mes donnees ou les emporter vers une autre application ?",
          a: "Oui. Tu peux exporter ton apercu vaccinal, par exemple en PDF. Nous etudions aussi des possibilites de connexions avec d'autres systemes afin que tes donnees restent utilisables.",
        },
        {
          q: "Que faire si je veux supprimer mon compte ?",
          a: "Tu peux supprimer ton compte a tout moment via les parametres. Toutes les donnees personnelles sont alors effacees. Des donnees anonymisees qui ne permettent pas de t'identifier peuvent etre conservees a des fins statistiques, par exemple pour montrer des tendances.",
        },
      ],
    },
  ],
};

const faqEs: FaqDoc = {
  title: "Preguntas frecuentes",
  intro:
    "Recibimos a menudo algunas preguntas sobre Vaxy. A continuacion encontraras las mas importantes con nuestras respuestas.",
  groups: [
    {
      heading: "General",
      items: [
        {
          q: "Por que deberia usar Vaxy?",
          a: "Muchas personas pierden sus justificantes de vacunacion o no tienen una vision completa de sus vacunas. Vaxy lo soluciona guardando todas tus vacunas de forma segura y clara, incluidas las de tu familia. Recibes recordatorios cuando una vacuna esta a punto de caducar y puedes conservar o compartir pruebas facilmente. Es util para visitas al medico o viajes.",
        },
        {
          q: "Necesito una cartilla de vacunacion?",
          a: "Solo necesitas una cartilla de vacunacion cuando viajas a paises donde es obligatoria. Una copia digital es recomendable por si pierdes la cartilla. Para fiebre amarilla, la prueba puede ser obligatoria en paises como Angola, Benin, Burkina Faso, Burundi, Camerun, Republica Centroafricana, Congo, Republica Democratica del Congo, Côte d'Ivoire (Costa de Marfil), Guinea Ecuatorial, Gabon, Gambia, Ghana, Guinea, Guinea-Bisau, Kenia, Liberia, Mali, Mauritania, Niger, Nigeria, Senegal, Sierra Leona, Sudan del Sur, Togo y Uganda; en Sudamerica aplica a Bolivia y Guayana Francesa. Paises como Surinam, Brasil, Ecuador, Colombia, Peru, Venezuela, Guyana, Paraguay, Panama, India, Australia, Singapur, China, Emiratos Arabes Unidos y Arabia Saudi tambien pueden exigir prueba si llegas desde un pais con riesgo de fiebre amarilla o has transitado por el. Las normas varian por pais y cambian con regularidad; compruebalas antes de viajar en ggdreisvaccinaties.nl o en una clinica oficial de viajes.",
        },
        {
          q: "Donde puedo solicitar mi historial de vacunacion?",
          a: "Puedes consultar parte de tus vacunas en mijn.rivm.nl iniciando sesion con DigiD, pero no todo esta siempre registrado alli. Las vacunas de ninos suelen registrarse en un portal sanitario como Mijn kinddossier o GGD Ouderportaal. Las vacunas de viaje suelen registrarse por clinica y puedes solicitarlas alli. Las vacunas administradas por tu medico de cabecera puedes solicitarlas a tu medico. Despues puedes importar vacunas facilmente tomando una foto; Vaxy las reconoce automaticamente.",
        },
        {
          q: "Tambien puedo concertar una cita para vacunas de viaje desde la app?",
          a: "Si, puedes concertar una cita para tus vacunas de viaje desde la app.",
        },
      ],
    },
    {
      heading: "Fiabilidad y seguridad",
      items: [
        {
          q: "Ayuda, mis datos han desaparecido. Como puede ser?",
          a: "Tus datos son almacenados de forma segura por nosotros y nunca los eliminamos nosotros. A veces los usuarios olvidan que metodo de inicio de sesion usaron, como Apple, Google, Facebook o correo electronico. En ese caso encontramos la cuenta. Si tienes una pregunta sobre tus datos, escribe a info@vaxy.app.",
        },
        {
          q: "Como garantiza Vaxy la privacidad de los datos medicos?",
          a: "Aplicamos minimizacion de datos: solo almacenamos los datos estrictamente necesarios para el funcionamiento de la app. Los datos medicos estan protegidos, nunca se usan comercialmente y no se comparten con terceros sin consentimiento explicito. El objetivo siempre es darte control sobre tus propios datos.",
        },
        {
          q: "Que medidas de seguridad utilizais?",
          a: "Seguimos mejores practicas internacionales y estandares de seguridad. Esto incluye cifrado de datos en reposo y en transito, acceso limitado y controlado a servidores, monitorizacion y registro continuos para detectar rapidamente anomalias, y actualizaciones y pruebas de seguridad periodicas para prevenir nuevos riesgos. Evaluamos periodicamente nuestras medidas y las adaptamos cuando surgen nuevas tecnicas o riesgos.",
        },
        {
          q: "Quien puede ver los datos medicos?",
          a: "Solo personas estrictamente autorizadas tienen acceso, y exclusivamente para trabajos tecnicos necesarios o desarrollo de la app. Nadie mas puede ver o usar tus datos. Tu sigues siendo siempre propietario de tus datos.",
        },
        {
          q: "Como haceis que la app siga siendo fiable?",
          a: "La app esta disenada para funcionar de forma segura y estable. Realizamos mejoras y actualizaciones continuas, probamos extensamente cada nueva version y trabajamos juntos para mantener altos estandares de calidad.",
        },
      ],
    },
    {
      heading: "Uso y funciones",
      items: [
        {
          q: "Que funciones ofrece la app?",
          a: "Las funciones principales son: registrar vacunas con marca, numero de lote, dosis y validez; recibir recordatorios cuando una vacuna esta a punto de caducar; subir o fotografiar justificantes de vacunacion para tu archivo; ver informacion sobre enfermedades infecciosas y el programa nacional neerlandes de vacunacion; exportar tu resumen de vacunacion, por ejemplo como PDF; y gestionar varios perfiles, por ejemplo de hijos o familiares.",
        },
        {
          q: "Para que puedo usar Vaxy?",
          a: "Con Vaxy mantienes todas tus vacunas organizadas en una sola app. Puedes registrar vacunas, configurar recordatorios y consultar informacion sobre enfermedades infecciosas. Asi tienes siempre una vision de tus propias vacunas y las de tus hijos.",
        },
        {
          q: "Se anadiran nuevas funciones?",
          a: "Si. Trabajamos continuamente en ampliar la app, como conexiones con instituciones sanitarias, la posibilidad de concertar citas en puntos de vacunacion conectados e informacion adicional basada en tendencias y estadisticas anonimas. La opinion de los usuarios desempena un papel importante en el desarrollo de nuevas funciones.",
        },
        {
          q: "Puedo anadir enfermedades infecciosas yo mismo en la app?",
          a: "No, no directamente. Falta algo? Envianos un correo a info@vaxy.app y evaluaremos si podemos anadirlo.",
        },
      ],
    },
    {
      heading: "Otros",
      items: [
        {
          q: "Como puedo contactar con vosotros?",
          a: "A traves del formulario de comentarios en la app o por correo a info@vaxy.app. Normalmente respondemos en un dia laborable.",
        },
        {
          q: "Puedo exportar mis datos o llevarlos a otra app?",
          a: "Si. Puedes exportar tu resumen de vacunacion, por ejemplo como PDF. Tambien estamos estudiando opciones de conexiones con otros sistemas para que tus datos sigan siendo utiles.",
        },
        {
          q: "Que pasa si quiero eliminar mi cuenta?",
          a: "Puedes eliminar tu cuenta en cualquier momento desde la configuracion. Entonces se borran todos los datos personales. Podemos conservar datos anonimizados que no puedan vincularse contigo con fines estadisticos, por ejemplo para mostrar tendencias.",
        },
      ],
    },
  ],
};

export const privacy: Record<Locale, LegalDoc> = copy(
  privacyNl,
  privacyEn,
  privacyDe,
  privacyFr,
  privacyEs,
);

export const cookies: Record<Locale, LegalDoc> = copy(
  cookiesNl,
  cookiesEn,
  cookiesDe,
  cookiesFr,
  cookiesEs,
);

export const terms: Record<Locale, LegalDoc> = copy(
  termsNl,
  termsEn,
  termsDe,
  termsFr,
  termsEs,
);

export const faq: Record<Locale, FaqDoc> = copy(
  faqNl,
  faqEn,
  faqDe,
  faqFr,
  faqEs,
);
